/* eslint-disable */

import React, { useEffect, useRef, useState, useMemo } from 'react';

const KZ_MODE_KEY = "kz_mode";
const KZ_MODE_EVENT = "kz:mode";
const KZ_SOUND_KEY = "kz_sound";

function kzCanUseDOM() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function kzReadStorage(key) {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

/* ── Hero H1 — static, streamlined ──────────────────────────────────────────
   Per-char fly-in caused spaces to render inconsistently (display:inline-block
   on space chars produced collapsed/asymmetric gaps). Reverted to a clean
   static render. A single subtle fade-in is applied to the whole heading on
   first paint via CSS opacity transition — no per-char movement, no fragments,
   no fly distance. Plays once and stays put.
   ───────────────────────────────────────────────────────────────────────── */
function injectHeroIntroStyles() {
  if (typeof document === "undefined" || document.getElementById("kz-hero-intro-style")) return;
  const s = document.createElement("style");
  s.id = "kz-hero-intro-style";
  s.textContent = `
    .kz-hero-h1 { opacity: 0; transform: translateY(4px); transition: opacity 0.45s ease-out, transform 0.45s ease-out; }
    .kz-hero-h1.kz-hero-h1-in { opacity: 1; transform: translateY(0); }
    .kz-prompt-glyph {
      color: var(--green, #00DC82);
      animation: kzPromptBreath 3.2s ease-in-out infinite;
    }
    @keyframes kzPromptBreath {
      0%, 100% { text-shadow: 0 0 6px rgba(0,220,130,0.25); }
      50% { text-shadow: 0 0 18px rgba(0,220,130,0.65), 0 0 4px rgba(0,220,130,0.9); }
    }
    @media (prefers-reduced-motion: reduce) {
      .kz-hero-h1 { opacity: 1; transform: none; transition: none; }
      .kz-prompt-glyph { animation: none; }
    }
  `;
  document.head.appendChild(s);
}

function useHeroIntro() {
  // Backward-compat shim: callers expect { ready, fragNodes }. We always render
  // the static heading and use a small ref-based opacity fade. fragNodes
  // intentionally empty — no decorative ghost characters.
  return { ready: true, fragNodes: [] };
}

function HeroH1Animated() {
  const titleRef = useRef(null);
  useEffect(() => {
    injectHeroIntroStyles();
    const el = titleRef.current;
    if (!el) return;
    // RAF to ensure paint before adding class — keeps the transition smooth.
    const id = requestAnimationFrame(() => el.classList.add("kz-hero-h1-in"));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <h1 className="h-display kz-hero-h1" ref={titleRef}>
      <span style={{ display: "block", fontFamily: "\"JetBrains Mono\"", fontSize: "clamp(2.4rem, 5.4vw, 4.1rem)", lineHeight: 1.0 }}>
        <span className="kz-prompt-glyph">{">"}</span>{" I deploy AI."}
      </span>
      <span className="h-display-sub" style={{ display: "block", color: "var(--fg-3)", paddingTop: 10, paddingLeft: "0.5em", letterSpacing: "-0.02em", fontWeight: 500, fontSize: "clamp(1.05rem, 4.6vw, 2.2rem)", lineHeight: 1.2, overflowWrap: "anywhere" }}>
        {"  "}no-human companies run by agents.
      </span>
    </h1>
  );
}

/* ---------- HERO SUBSCRIBE FORM — CC-867 ---------- */
/* Inline `$ subscribe` form directly below the hero typewriter.
 * Operator voice: no modal, no "join our newsletter", just a prompt + input.
 * Fires Plausible custom event `email_signup` on success. */
function HeroSubscribeForm() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | err
  const [errMsg, setErrMsg] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || state === "loading" || state === "done") return;
    setState("loading");
    setErrMsg("");
    try {
      const res = await fetch("/api/newsletter-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "hero" }),
      });
      if (res.ok) {
        setState("done");
        window.plausible?.("email_signup", { props: { audience: "kevinzai", source: "hero" } });
      } else {
        const d = await res.json().catch(() => ({}));
        setErrMsg(d.error === "invalid_email" ? "invalid email" : "try again");
        setState("err");
      }
    } catch {
      setErrMsg("network err");
      setState("err");
    }
  }

  if (state === "done") {
    return (
      <div className="mt-32 hero-subscribe hero-subscribe--done" style={{ fontSize: 14, color: "var(--green, #00DC82)" }}>
        <span className="dim">$ subscribed</span>{" "}
        <span>✓ {email} — check your inbox.</span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-32 hero-subscribe" style={{ fontSize: 14, lineHeight: 1.6 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <span className="dim" style={{ fontFamily: "\"JetBrains Mono\", ui-monospace, Menlo, monospace" }}>$ subscribe</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@host"
          required
          autoComplete="email"
          inputMode="email"
          disabled={state === "loading"}
          className="hero-subscribe-input"
          style={{
            background: "transparent",
            border: "1px solid var(--fg-3, #8a8676)",
            color: "var(--fg, #e8e6dc)",
            fontFamily: "\"JetBrains Mono\", ui-monospace, Menlo, monospace",
            fontSize: 14,
            padding: "6px 10px",
            minWidth: 220,
            flex: "1 1 220px",
            maxWidth: 360,
          }}
          aria-label="Email address"
        />
        <button
          type="submit"
          disabled={state === "loading" || !email}
          className="hero-subscribe-btn"
          style={{
            background: "var(--amber, #f5f3ec)",
            color: "var(--bg, #000000)",
            border: "none",
            padding: "6px 14px",
            fontFamily: "\"JetBrains Mono\", ui-monospace, Menlo, monospace",
            fontSize: 14,
            fontWeight: 600,
            cursor: state === "loading" ? "wait" : "pointer",
            letterSpacing: "0.02em",
          }}
        >
          {state === "loading" ? "..." : "→"}
        </button>
      </div>
      <div className="mt-8" style={{ fontSize: 12, color: "var(--fg-3, #8a8676)" }}>
        agent infrastructure ops. 1x/week. unsubscribe anytime.
      </div>
      {state === "err" && (
        <div className="mt-8" style={{ fontSize: 12, color: "var(--red, #d56565)" }}>
          ✗ {errMsg}
        </div>
      )}
    </form>
  );
}

function kzWriteStorage(key, value) {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (_) {
    return false;
  }
}

function readKzMode() {
  // ?mode=exec in URL takes priority — enables direct-link sharing of EXEC view
  if (typeof window !== "undefined") {
    const urlMode = new URLSearchParams(window.location.search).get("mode");
    if (urlMode === "exec") return "exec";
    if (urlMode === "builder") return "builder";
  }
  return kzReadStorage(KZ_MODE_KEY) === "exec" ? "exec" : "builder";
}

function normalizeKzMode(mode) {
  return mode === "exec" ? "exec" : "builder";
}

function setKzParticlesEnabled(enabled) {
  if (typeof window === "undefined" || !window.kzParticles) return;
  const api = window.kzParticles;
  try {
    if (enabled) {
      if (api.enable) api.enable();
      else if (api.resume) api.resume();
    } else {
      if (api.disable) api.disable();
      else {
        if (api.pause) api.pause();
        if (api.clear) api.clear();
      }
    }
  } catch (_) {}
}

function applyKzMode(mode) {
  if (!kzCanUseDOM() || !document.body) return;
  const clean = normalizeKzMode(mode);
  document.body.dataset.mode = clean;
  document.body.style.setProperty("--scan-opacity", clean === "exec" ? "0" : "0.6");
  setKzParticlesEnabled(clean !== "exec");
}

function publishKzMode(mode) {
  if (typeof window === "undefined") return;
  const clean = normalizeKzMode(mode);
  window.dispatchEvent(new CustomEvent(KZ_MODE_EVENT, { detail: { mode: clean } }));
}

function useKzMode() {
  const [mode, setModeState] = useState(() => readKzMode());

  useEffect(() => {
    applyKzMode(mode);
    kzWriteStorage(KZ_MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onMode = (event) => {
      const next = normalizeKzMode(event.detail && event.detail.mode);
      setModeState(next);
      applyKzMode(next);
    };
    const onStorage = (event) => {
      if (event.key === KZ_MODE_KEY) {
        const next = normalizeKzMode(event.newValue);
        setModeState(next);
        applyKzMode(next);
      }
    };
    window.addEventListener(KZ_MODE_EVENT, onMode);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(KZ_MODE_EVENT, onMode);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setMode = (nextMode) => {
    const clean = normalizeKzMode(nextMode);
    setModeState(clean);
    kzWriteStorage(KZ_MODE_KEY, clean);
    applyKzMode(clean);
    publishKzMode(clean);
  };

  return [mode, setMode];
}

function usePrefersReducedMotion() {
  const read = () => (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [reduced, setReduced] = useState(read);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mql.matches);
    onChange();
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return reduced;
}

function easeOutExpo(t) {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function injectWaveBStyles() {
  if (!kzCanUseDOM() || document.getElementById("kz-wave-b-styles")) return;
  const style = document.createElement("style");
  style.id = "kz-wave-b-styles";
  style.textContent = `
    body::before{ opacity: var(--scan-opacity, 0.6) !important; }
    body[data-mode="exec"]{ --scan-opacity: 0; }
    body[data-mode="exec"] .particle-field{ opacity: 0 !important; }
    body[data-mode="exec"] .biz-face{ display: none !important; }
    body[data-mode="exec"] .biz-grid{ grid-template-columns: minmax(0, 1fr) !important; }
    body[data-mode="exec"] .biz-card{ width: min(430px, 100%); }
    body[data-theme="auto"] section[data-section="hero"],
    body[data-theme="auto"] section[data-section="projects"],
    body[data-theme="auto"] section[data-section="writing"],
    body[data-theme="auto"] section[data-section="newsletter"],
    body[data-theme="auto"] section[data-section="contact"],
    body[data-theme="auto"] section[data-section="verified"]{
      --amber: var(--fg);
      --amber-d: var(--fg-2);
    }

    .kz-topbar-controls{
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .kz-mode-toggle{
      display: inline-grid;
      grid-template-columns: repeat(2, auto);
      align-items: stretch;
      height: 24px;
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      flex: none;
    }
    .kz-mode-toggle button,
    .kz-topbar-pill{
      height: 22px;
      display: inline-flex;
      align-items: center;
      place-content: center;
      border: 0;
      color: var(--fg-4);
      font-family: var(--mono);
      font-size: 10px;
      line-height: 1;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      white-space: nowrap;
    }
    .kz-mode-toggle button{
      padding: 0 8px;
      border-right: 1px solid var(--line);
    }
    .kz-mode-toggle button:last-child{ border-right: 0; }
    .kz-mode-toggle button:hover,
    .kz-topbar-pill:hover{
      color: var(--amber);
      background: color-mix(in oklch, var(--amber) 6%, transparent);
    }
    .kz-mode-toggle button.is-active{
      color: var(--bg);
      background: var(--amber);
    }
    .kz-topbar-pill{
      height: 24px;
      padding: 0 9px;
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      flex: none;
    }
    .kz-sound-toggle{
      width: 28px;
      padding: 0;
      font-size: 13px;
      letter-spacing: 0;
    }
    .kz-sound-toggle.is-on{
      color: var(--bg);
      background: var(--cyan);
      border-color: var(--cyan);
    }

    .kz-agent-complete-flash{
      border-color: var(--green) !important;
      background: color-mix(in oklch, var(--green) 18%, transparent) !important;
      color: var(--green) !important;
      box-shadow: 0 0 14px color-mix(in oklch, var(--green) 48%, transparent);
    }
    .heatmap.spells-number .cell{
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      transition: background 320ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
    }
    .heatmap.spells-number{
      display: grid;
      grid-template-columns: repeat(40, 1fr);
      grid-template-rows: repeat(7, 1fr);
      aspect-ratio: 40 / 7;
      gap: 2px;
      width: 100%;
      max-width: 360px;
      margin-top: 8px;
    }

    .verified-strip{
      padding: 10px 0 20px;
      border-top: 1px solid var(--line);
      border-bottom: 1px solid var(--line);
      background: var(--bg);
    }
    .verified-row{
      min-height: 28px;
      display: flex;
      align-items: center;
      gap: 8px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .verified-row::-webkit-scrollbar{ display: none; }
    .verified-chip{
      height: 28px;
      display: inline-flex;
      align-items: center;
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      color: var(--fg-3);
      padding: 0 10px;
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      white-space: nowrap;
      position: relative;
      flex: none;
    }
    .verified-chip:hover{
      color: var(--fg);
      border-color: var(--amber);
    }
    .verified-chip::after{
      content: attr(data-tip);
      position: absolute;
      left: 0;
      top: calc(100% + 7px);
      z-index: 80;
      width: max-content;
      max-width: min(320px, 82vw);
      border: 1px solid var(--line-strong);
      background: var(--bg-2);
      color: var(--fg-2);
      padding: 7px 9px;
      font-size: 10px;
      line-height: 1.45;
      letter-spacing: 0.04em;
      text-transform: none;
      opacity: 0;
      pointer-events: none;
      transform: translateY(-2px);
      transition: opacity 120ms ease, transform 120ms ease;
    }
    .verified-chip:hover::after,
    .verified-chip:focus-visible::after{
      opacity: 1;
      transform: translateY(0);
    }

    .case-study-grid{
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      margin-top: 28px;
    }
    .case-study .body{
      padding: 14px;
      font-size: 11px;
      color: var(--fg-3);
      line-height: 1.55;
    }
    .case-study-title{
      color: var(--fg);
      font-size: 13px;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin-bottom: 10px;
    }
    .case-line{
      display: grid;
      grid-template-columns: 68px 1fr;
      gap: 10px;
      padding: 7px 0;
      border-top: 1px dashed var(--line);
    }
    .case-line:first-of-type{ border-top: 0; }
    .case-k{
      color: var(--fg-4);
      letter-spacing: 0.14em;
      text-transform: uppercase;
      font-size: 9px;
    }
    .case-outcome{
      color: var(--green);
      text-shadow: 0 0 10px color-mix(in oklch, var(--green) 24%, transparent);
    }

    .exec-cover{
      min-height: 200px;
      border-bottom: 1px solid var(--line-strong);
      background:
        linear-gradient(90deg, color-mix(in oklch, var(--amber) 10%, transparent) 0 1px, transparent 1px 44px),
        linear-gradient(0deg, color-mix(in oklch, var(--fg) 5%, transparent) 0 1px, transparent 1px 18px),
        var(--bg);
      position: relative;
      overflow: hidden;
    }
    .exec-cover::before{
      content: "KZ / EXEC / FLEET / COMMANDER / OPENCLAW / HERMES / ";
      position: absolute;
      left: -2ch;
      right: -2ch;
      top: 24px;
      color: var(--fg-4);
      font-size: 11px;
      letter-spacing: 0.22em;
      white-space: nowrap;
      opacity: 0.34;
    }
    .exec-cover::after{
      content: "";
      position: absolute;
      inset: auto 0 0;
      height: 72px;
      background: linear-gradient(180deg, transparent, var(--bg));
    }
    .exec-cover-inner{
      position: relative;
      min-height: 200px;
    }
    .exec-cover-tag{
      position: absolute;
      right: 0;
      top: 24px;
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      color: var(--fg-3);
      padding: 8px 10px;
      font-size: 10px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
    }
    .exec-cover-card{
      position: absolute;
      right: 0;
      bottom: 24px;
      width: min(340px, 100%);
      border: 1px solid var(--line);
      background: color-mix(in oklch, var(--bg-1) 84%, transparent);
      padding: 12px;
      color: var(--fg-3);
      font-size: 11px;
      line-height: 1.7;
    }
    .exec-profile-section{
      padding-top: 0;
      padding-bottom: 72px;
    }
    .exec-profile-card{
      margin-top: -62px;
      position: relative;
      z-index: 2;
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      box-shadow: 0 24px 70px color-mix(in oklch, var(--bg) 86%, transparent);
    }
    .exec-profile-main{
      display: grid;
      grid-template-columns: 162px minmax(0, 1fr) minmax(160px, 220px);
      gap: 22px;
      padding: 22px;
      align-items: start;
    }
    .exec-avatar{
      width: 162px;
      aspect-ratio: 1 / 1;
      border: 1px solid var(--line-strong);
      background: var(--bg);
      overflow: hidden;
      display: grid;
      place-items: center;
      padding: 10px;
    }
    .exec-avatar .ascii-portrait,
    .exec-avatar canvas{
      max-width: 100% !important;
      max-height: 100% !important;
      width: 100% !important;
      height: auto !important;
    }
    .exec-avatar img{
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .exec-name-row{
      display: flex;
      align-items: flex-start;
      gap: 12px;
      flex-wrap: wrap;
    }
    .exec-name{
      margin: 0;
      color: var(--fg);
      font-size: clamp(1.75rem, 4vw, 3.1rem);
      line-height: 0.95;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 800;
    }
    .exec-badge{
      border: 1px solid var(--line-strong);
      background: var(--bg);
      color: var(--green);
      padding: 6px 8px;
      font-size: 10px;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      white-space: nowrap;
    }
    .exec-headline{
      margin-top: 12px;
      color: var(--fg-2);
      font-size: 15px;
      line-height: 1.55;
    }
    .exec-location{
      margin-top: 8px;
      color: var(--fg-3);
      font-size: 12px;
      letter-spacing: 0.08em;
    }
    .exec-stat-row{
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 18px;
    }
    .exec-stat-chip{
      border: 1px solid var(--line);
      background: var(--bg);
      color: var(--fg-3);
      padding: 7px 9px;
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .exec-stat-chip strong{
      color: var(--fg);
      font-size: 12px;
      letter-spacing: 0.02em;
    }
    .exec-cta-row{
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 20px;
    }
    .exec-link-rail{
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }
    .exec-link-icon{
      min-height: 44px;
      border: 1px solid var(--line);
      background: var(--bg);
      color: var(--fg-3);
      text-decoration: none;
      display: grid;
      place-items: center;
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .exec-link-icon:hover{
      color: var(--amber);
      border-color: var(--line-strong);
      transform: translateY(-1px);
    }
    .exec-profile-body{
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(280px, 430px);
      gap: 22px;
      margin-top: 24px;
      align-items: start;
    }
    .exec-main-stack{
      display: grid;
      gap: 18px;
    }
    .exec-panel{
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      padding: 20px;
    }
    .exec-panel-head{
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 16px;
      color: var(--fg-4);
      font-size: 10px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
    }
    .exec-panel-head .rule{
      height: 1px;
      flex: 1;
      background: var(--line);
    }
    .exec-about-copy{
      color: var(--fg-2);
      font-size: 14px;
      line-height: 1.75;
      max-width: 76ch;
      margin: 0;
    }
    .exec-interest-row{
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 18px;
    }
    .exec-interest-chip{
      border: 1px solid var(--line);
      color: var(--fg-3);
      background: var(--bg);
      padding: 7px 9px;
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .exec-featured-grid{
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
    }
    .exec-featured-card{
      display: grid;
      min-height: 238px;
      border: 1px solid var(--line);
      background: var(--bg);
      text-decoration: none;
      color: inherit;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease;
    }
    .exec-featured-card:hover{
      transform: translateY(-3px);
      border-color: var(--line-strong);
      background: color-mix(in oklch, var(--bg-1) 70%, var(--bg));
    }
    .exec-feature-thumb{
      min-height: 86px;
      border-bottom: 1px solid var(--line);
      background:
        linear-gradient(135deg, color-mix(in oklch, var(--amber) 13%, transparent), transparent 52%),
        repeating-linear-gradient(90deg, color-mix(in oklch, var(--fg) 8%, transparent) 0 1px, transparent 1px 12px),
        var(--bg-1);
      display: flex;
      align-items: flex-end;
      padding: 10px;
    }
    .exec-feature-metric{
      border: 1px solid var(--line-strong);
      background: var(--bg);
      color: var(--green);
      padding: 6px 8px;
      font-size: 10px;
      line-height: 1.35;
    }
    .exec-feature-body{
      padding: 14px;
    }
    .exec-feature-title{
      color: var(--fg);
      font-size: 15px;
      font-weight: 700;
      margin-bottom: 9px;
    }
    .exec-feature-subhead{
      color: var(--fg-3);
      font-size: 12px;
      line-height: 1.6;
    }
    .exec-feature-stack{
      margin-top: 14px;
      color: var(--fg-4);
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .exec-experience-list{
      display: grid;
      gap: 0;
      border-top: 1px solid var(--line);
    }
    .exec-exp-row{
      display: grid;
      grid-template-columns: 18px minmax(120px, 0.9fr) minmax(120px, 0.8fr) minmax(120px, 0.7fr) minmax(180px, 1.25fr);
      gap: 12px;
      align-items: center;
      padding: 15px 0;
      border-bottom: 1px solid var(--line);
    }
    .exec-exp-dot{
      width: 9px;
      height: 9px;
      border-radius: 999px;
      background: var(--exec-brand-c, var(--amber));
      box-shadow: 0 0 12px color-mix(in oklch, var(--exec-brand-c, var(--amber)) 40%, transparent);
    }
    .exec-exp-name{
      color: var(--fg);
      font-weight: 700;
      font-size: 13px;
    }
    .exec-exp-role,
    .exec-exp-date,
    .exec-exp-metric{
      color: var(--fg-3);
      font-size: 11px;
      line-height: 1.45;
    }
    .exec-exp-metric{
      color: var(--fg-2);
    }
    .exec-contact-rail{
      position: sticky;
      top: 86px;
      display: grid;
      gap: 12px;
    }
    .exec-contact-note{
      border: 1px solid var(--line-strong);
      background: var(--bg-1);
      padding: 14px;
      color: var(--fg-3);
      font-size: 11px;
      line-height: 1.65;
    }
    .exec-contact-note strong{
      color: var(--fg);
      display: block;
      margin-bottom: 6px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      font-size: 10px;
    }

    .newsletter-preview{
      margin-top: 22px;
      margin-bottom: 24px;
      max-width: 720px;
      border: 1px solid var(--line-strong);
      background: var(--bg);
      font-family: var(--mono);
    }
    .newsletter-preview-head{
      display: flex;
      place-content: space-between;
      gap: 12px;
      padding: 8px 10px;
      border-bottom: 1px solid var(--line);
      color: var(--fg-4);
      font-size: 10px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    .newsletter-preview-body{
      padding: 14px 16px 16px;
      font-size: 12px;
      line-height: 1.62;
      color: var(--fg-3);
    }
    .newsletter-preview-title{
      color: var(--fg);
      font-weight: 700;
      margin-bottom: 9px;
    }
    .newsletter-preview-lines{
      margin: 0;
      white-space: pre-wrap;
      color: var(--fg-3);
      font: inherit;
    }
    .newsletter-preview a{
      display: inline-flex;
      margin-top: 12px;
      color: var(--amber);
      font-size: 11px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    @media (max-width: 980px){
      .case-study-grid{ grid-template-columns: 1fr; }
      .exec-profile-main,
      .exec-profile-body{
        grid-template-columns: 1fr;
      }
      .exec-profile-main{
        justify-items: center;
        text-align: left;
        gap: 18px;
      }
      .exec-avatar{
        width: clamp(220px, 48vw, 320px);
        margin: 0 auto;
      }
      .exec-name-row,
      .exec-headline,
      .exec-location,
      .exec-stat-row,
      .exec-cta-row{
        justify-self: start;
        width: 100%;
      }
      .exec-link-rail{
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      .exec-featured-grid{
        grid-template-columns: 1fr;
      }
      .exec-exp-row{
        grid-template-columns: 18px minmax(0, 1fr);
        align-items: start;
      }
      .exec-exp-role,
      .exec-exp-date,
      .exec-exp-metric{
        grid-column: 2;
      }
      .exec-contact-rail{
        position: static;
      }
    }
    @media (max-width: 700px){
      .topbar .container{ padding-left: 12px; padding-right: 12px; }
      .topbar-inner{ gap: 8px; }
      .topbar .brand span:not(.sigil){ display: none; }
      .topbar .status-cluster{ gap: 6px; place-content: flex-end; min-width: 0; }
      .kz-topbar-time,
      .kz-topbar-loc,
      .kz-topbar-dot{ display: none; }
      .kz-mode-toggle button{ padding: 0 6px; font-size: 9px; letter-spacing: 0.08em; }
      .kz-topbar-pill{ padding: 0 7px; font-size: 9px; letter-spacing: 0.08em; }
      .kz-sound-toggle{ width: 26px; font-size: 12px; }
      .verified-strip{ padding-bottom: 14px; }
      .newsletter-preview-head{ flex-direction: column; gap: 2px; }
      .exec-cover-tag,
      .exec-cover-card{
        right: 12px;
        left: 12px;
        width: auto;
      }
      .exec-profile-card{
        margin-top: -46px;
      }
      .exec-profile-main{
        padding: 18px;
        gap: 16px;
      }
      .exec-avatar{
        width: clamp(220px, 64vw, 300px);
        padding: 8px;
      }
      .exec-name{
        font-size: clamp(1.6rem, 9vw, 2.4rem);
      }
      .exec-link-rail{
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .exec-panel{
        padding: 16px;
      }
    }
  `;
  document.head.appendChild(style);
}

injectWaveBStyles();
if (kzCanUseDOM()) applyKzMode(readKzMode());

/* eslint-disable */
/* ---------- ASCII deco glyphs (decorative section accents) ---------- */
const ASCII_GLYPHS = {
  diamond: "◆",
  block:   "▣",
  dots:    "▢",
  arrow:   "▸",
  bracket: "❯",
  square:  "□",
  pixel:   "▮",
  hash:    "▦",
};
function AsciiBadge({ glyph = "diamond", count = 3, color = "var(--amber)" }) {
  const g = ASCII_GLYPHS[glyph] || glyph;
  return (
    <span aria-hidden="true" style={{ color, letterSpacing: "0.15em", fontFamily: "var(--mono)", marginRight: 8 }}>
      {Array.from({ length: count }).map((_, i) => g).join("")}
    </span>
  );
}
function AsciiRule({ char = "─", color = "var(--line)", glow = false, label, num }) {
  /* full-width ascii horizontal rule.
     With label + num props renders a chapter marker:
       ── 04 ─────────────── readyiq ─────────────── ──
  */
  const baseStyle = {
    fontFamily: "var(--mono)",
    fontSize: 11,
    color,
    whiteSpace: "nowrap",
    overflow: "hidden",
    letterSpacing: 0,
    textShadow: glow ? "0 0 6px " + color : "none",
    userSelect: "none",
    lineHeight: 1
  };

  if (label && num) {
    const upperLabel = label.toUpperCase();
    const prefix = `── ${num} `;
    const infix = ` ${upperLabel} `;
    const padSide = 19; /* ─ chars on each side of the label */
    const dashedPad = char.repeat(padSide);
    const suffix = " ──";
    return (
      <div className="ascii-chapter" style={{ ...baseStyle, color: color === "var(--line)" ? "var(--fg-3)" : color }}>
        {prefix}{dashedPad}{infix}{dashedPad}{suffix}
      </div>
    );
  }

  return (
    <div style={baseStyle}>
      {char.repeat(220)}
    </div>
  );
}

/* ---------- ASCII cover generator for writing posts ---------- */
function AsciiCover({ title = "untitled", tag = "POST", seed = 1, art = null }) {
  const cols = 40;

  // If concrete ASCII art is provided (a flowchart, diagram, etc.), render
  // that inside the cover frame instead of generating noise — same chrome,
  // real content. Lines are padded/truncated to the frame width and a few
  // "scanline" filler rows are appended to keep card heights consistent.
  if (art && typeof art === "string") {
    const raw = art.replace(/\t/g, "  ").split("\n");
    const target = 14; // rows in the cover, matches the noise variant
    const padded = [];
    for (let i = 0; i < target; i++) {
      const src = raw[i] || "";
      // truncate or right-pad to cols-2 (frame eats 2 chars)
      const inner = src.length > cols - 2 ? src.slice(0, cols - 2) : src + " ".repeat(cols - 2 - src.length);
      padded.push(inner);
    }
    return (
      <pre style={{
        margin: 0,
        fontFamily: "var(--mono)",
        fontSize: 9,
        lineHeight: 1.15,
        color: "var(--fg-2)",
        whiteSpace: "pre",
        letterSpacing: "0.04em"
      }}>
        <span style={{ color: "var(--amber)" }}>{`┌${"─".repeat(cols - 2)}┐`}</span>
        {"\n"}
        {padded.map((l, i) => (
          <span key={i}>
            <span style={{ color: "var(--amber)" }}>│</span>
            <span style={{ color: i < 2 ? "var(--fg)" : "var(--fg-2)" }}>{l}</span>
            <span style={{ color: "var(--amber)" }}>│</span>
            {"\n"}
          </span>
        ))}
        <span style={{ color: "var(--amber)" }}>{`└${"─".repeat(cols - 5)}<${tag.padEnd(2).slice(0, 2).toUpperCase()}┘`}</span>
      </pre>
    );
  }

  /* deterministic-ish noise pattern based on seed (fallback) */
  const rows = 14;
  const chars = ".·:-=+*x#@▓░▒█";
  const lines = [];
  for (let y = 0; y < rows; y++) {
    let line = "";
    for (let x = 0; x < cols; x++) {
      /* pseudo-noise: distance from a moving sine wave */
      const cx = cols / 2 + Math.sin(seed * 0.7 + y * 0.3) * 12;
      const cy = rows / 2 + Math.cos(seed * 0.9 + x * 0.2) * 4;
      const d = Math.hypot(x - cx, y - cy);
      const n = Math.floor((Math.sin(seed + x * 0.4) + Math.cos(seed + y * 0.5)) * 4 + d * 0.5);
      const idx = ((n % chars.length) + chars.length) % chars.length;
      line += chars[idx];
    }
    lines.push(line);
  }
  return (
    <pre style={{
      margin: 0,
      fontFamily: "var(--mono)",
      fontSize: 9,
      lineHeight: 1.1,
      color: "var(--fg-3)",
      whiteSpace: "pre",
      letterSpacing: "0.04em"
    }}>
      <span style={{ color: "var(--amber)" }}>{`┌${"─".repeat(cols - 2)}┐`}</span>
      {"\n"}
      {lines.map((l, i) => (
        <span key={i}>
          <span style={{ color: "var(--amber)" }}>│</span>
          <span style={{ color: i < 3 ? "var(--fg)" : "var(--fg-3)" }}>{l.slice(1, -1)}</span>
          <span style={{ color: "var(--amber)" }}>│</span>
          {"\n"}
        </span>
      ))}
      <span style={{ color: "var(--amber)" }}>{`└${"─".repeat(cols - 5)}<${tag.padEnd(2).slice(0, 2).toUpperCase()}┘`}</span>
    </pre>
  );
}

/* ---------- Reveal-on-scroll ---------- */
function Reveal({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    // If already in viewport on mount, show immediately
    const r = ref.current.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      setTimeout(() => setShown(true), delay);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setTimeout(() => setShown(true), delay);
          obs.disconnect();
        }
      });
    }, { threshold: 0.05, rootMargin: "0px 0px -40px 0px" });
    obs.observe(ref.current);
    // Safety net: ensure visible after 1.5s no matter what
    const fallback = setTimeout(() => setShown(true), 1500 + delay);
    return () => {obs.disconnect();clearTimeout(fallback);};
  }, [delay]);
  return (
    <Tag ref={ref} className={`reveal ${shown ? "in" : ""} ${className}`} {...rest}>
      {children}
    </Tag>);

}

/* ---------- Counter ---------- */
function CountUp({ to, duration = 1200, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(Math.round(eased * to));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

/* ---------- Typewriter ---------- */
function Typewriter({ phrases }) {
  const reduced = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing");
  useEffect(() => {
    if (reduced) return;
    let t;
    const cur = phrases[idx];
    if (phase === "typing") {
      if (text.length < cur.length) t = setTimeout(() => setText(cur.slice(0, text.length + 1)), 50);else
      t = setTimeout(() => setPhase("hold"), 1700);
    } else if (phase === "hold") t = setTimeout(() => setPhase("deleting"), 1200);else
    if (phase === "deleting") {
      if (text.length > 0) t = setTimeout(() => setText(text.slice(0, -1)), 26);else
      {setPhase("typing");setIdx((i) => (i + 1) % phrases.length);}
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, phrases, reduced]);
  if (reduced) return <span className="amber">{phrases[0]}</span>;
  return <span className="amber">{text}<span className="cursor" /></span>;
}

/* ---------- Heatmap (GitHub-contribution style) ----------
   `pattern` = string of digits 0-4 (one per cell, row-major over 7 rows × cols).
   `cols`    = number of columns; `rows` defaults to 7. If pattern shorter, repeats.
   `seed`    = if pattern omitted, generate a believable activity pattern. */
function Heatmap({ pattern, cols = 28, rows = 7, count, size = "", seed = 0, className = "", style, alive = true }) {
  // count mode: GitHub-contribution style. Every tile is a FIXED-SIZE grid
  // (7 rows × 40 cols = 280 cells). The first `count` cells are lit (with
  // varied intensity for the "alive" feel); the rest stay dim (l0). All 4
  // stats render at identical tile dimensions — only the fill density
  // changes. 5 lit = sparse scatter; 280 lit = full board.
  const COUNT_GRID = { rows: 7, cols: 40, total: 280 };
  const dims = useMemo(() => {
    if (count == null) return { c: cols, r: rows };
    return { c: COUNT_GRID.cols, r: COUNT_GRID.rows };
  }, [count, cols, rows]);
  const total = count != null ? COUNT_GRID.total : dims.c * dims.r;
  const baseCells = useMemo(() => {
    const out = [];
    if (count != null) {
      const lit = Math.min(COUNT_GRID.total, Math.max(0, count | 0));
      // Pick `lit` random cell positions to light up so the pattern looks
      // organic (not a left-anchored block). Use a deterministic PRNG so the
      // pattern is stable per stat across re-renders.
      let s = (lit + 7) * 9301 + 49297;
      const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
      // Fisher-Yates partial shuffle of indices 0..total-1, take first `lit`.
      const idx = new Array(COUNT_GRID.total);
      for (let i = 0; i < COUNT_GRID.total; i++) idx[i] = i;
      for (let i = 0; i < lit; i++) {
        const j = i + Math.floor(rand() * (COUNT_GRID.total - i));
        const tmp = idx[i]; idx[i] = idx[j]; idx[j] = tmp;
      }
      const litSet = new Set(idx.slice(0, lit));
      for (let i = 0; i < COUNT_GRID.total; i++) {
        if (litSet.has(i)) {
          const r = rand();
          out.push(r > 0.65 ? 4 : r > 0.35 ? 3 : 2);
        } else {
          out.push(0);
        }
      }
      return out;
    }
    if (pattern && pattern.length) {
      const clean = pattern.replace(/\s/g, "");
      for (let i = 0; i < total; i++) {
        out.push(parseInt(clean[i % clean.length], 10) || 0);
      }
      return out;
    }
    let s = seed * 9301 + 49297;
    const rand = () => {s = (s * 9301 + 49297) % 233280;return s / 233280;};
    for (let i = 0; i < total; i++) {
      const r = rand();
      out.push(r > 0.85 ? 4 : r > 0.65 ? 3 : r > 0.4 ? 2 : r > 0.18 ? 1 : 0);
    }
    return out;
  }, [pattern, dims.c, dims.r, total, count, seed]);

  const [cells, setCells] = useState(baseCells);
  useEffect(() => { setCells(baseCells); }, [baseCells]);

  // "Alive": every ~700-1100ms, pick 1-3 cells and bump them. Then decay.
  // Bump pool is bounded — in count mode only the first `count` cells animate
  // (so 280-cell heatmaps don't waste cycles on the dim trailing slots).
  useEffect(() => {
    if (!alive) return;
    let cancel = false;
    const pool = count != null ? Math.min(count, baseCells.length) : baseCells.length;
    if (pool < 1) return;
    const tick = () => {
      if (cancel) return;
      setCells((prev) => {
        const next = prev.slice();
        const bumps = 1 + Math.floor(Math.random() * 3);
        for (let b = 0; b < bumps; b++) {
          const i = Math.floor(Math.random() * pool);
          const base = baseCells[i] || 0;
          if (Math.random() > 0.4) {
            next[i] = Math.min(4, Math.max(next[i], base) + 1);
          } else if (next[i] > 0) {
            next[i] = Math.max(0, next[i] - 1);
          }
        }
        return next;
      });
      const wait = 600 + Math.random() * 700;
      setTimeout(tick, wait);
    };
    const id = setTimeout(tick, 400 + Math.random() * 600);
    return () => { cancel = true; clearTimeout(id); };
  }, [alive, baseCells, count]);

  return (
    <div
      className={`heatmap ${size} ${alive ? "alive" : ""} ${count != null ? "count-mode" : ""} ${className}`}
      style={count != null ? {
        // CSS handles layout in count-mode (see .heatmap.count-mode rule)
        ...style
      } : {
        gridTemplateRows: `repeat(${dims.r}, 1fr)`,
        ...style
      }}
      aria-hidden="true">
      {cells.map((lvl, i) =>
      <span key={i} className={`cell l${lvl}`} />
      )}
    </div>);

}

const DIGITS = {
  "0": ["0110", "1001", "1001", "1001", "1001", "1001", "0110"],
  "1": ["0100", "1100", "0100", "0100", "0100", "0100", "1110"],
  "2": ["1110", "0001", "0001", "0110", "1000", "1000", "1111"],
  "3": ["1110", "0001", "0001", "0110", "0001", "0001", "1110"],
  "4": ["1001", "1001", "1001", "1111", "0001", "0001", "0001"],
  "5": ["1111", "1000", "1000", "1110", "0001", "0001", "1110"],
  "6": ["0111", "1000", "1000", "1110", "1001", "1001", "0110"],
  "7": ["1111", "0001", "0010", "0010", "0100", "0100", "0100"],
  "8": ["0110", "1001", "1001", "0110", "1001", "1001", "0110"],
  "9": ["0110", "1001", "1001", "0111", "0001", "0001", "1110"],
  ".": ["0000", "0000", "0000", "0000", "0000", "0110", "0110"],
  "+": ["0000", "0100", "0100", "1110", "0100", "0100", "0000"],
  "M": ["1001", "1111", "1111", "1001", "1001", "1001", "1001"],
  "$": ["0111", "1010", "1010", "0110", "0011", "1010", "1110"],
  "%": ["1001", "0010", "0010", "0100", "0100", "1001", "0000"],
  " ": ["0000", "0000", "0000", "0000", "0000", "0000", "0000"],
};

function heatmapTextPattern(text, cols = 40, rows = 7, progress = 1) {
  const grid = Array.from({ length: rows }, () => Array(cols).fill(1));
  const chars = String(text || "").toUpperCase().split("");
  const width = Math.max(0, chars.reduce((sum, ch) => {
    const glyph = DIGITS[ch] || DIGITS[" "];
    return sum + glyph[0].length + 1;
  }, -1));
  let x = Math.max(0, Math.floor((cols - width) / 2));
  const lit = [];

  chars.forEach((ch) => {
    const glyph = DIGITS[ch] || DIGITS[" "];
    glyph.forEach((row, y) => {
      for (let dx = 0; dx < row.length; dx++) {
        if (row[dx] === "1" && y < rows && x + dx >= 0 && x + dx < cols) {
          lit.push([y, x + dx]);
        }
      }
    });
    x += glyph[0].length + 1;
  });

  const visible = Math.ceil(lit.length * Math.max(0, Math.min(1, progress)));
  for (let i = 0; i < visible; i++) {
    const [y, col] = lit[i];
    grid[y][col] = 4;
  }

  // .heatmap uses grid-auto-flow: column, so emit cells column-major.
  const levels = [];
  for (let col = 0; col < cols; col++) {
    for (let y = 0; y < rows; y++) levels.push(String(grid[y][col]));
  }
  return levels.join("");
}

function flashAgentCountTile() {
  if (!kzCanUseDOM()) return;
  const nodes = document.querySelectorAll(".biz-status-badge, .biz-card .footer .live");
  nodes.forEach((node) => node.classList.add("kz-agent-complete-flash"));
  window.setTimeout(() => {
    nodes.forEach((node) => node.classList.remove("kz-agent-complete-flash"));
  }, 200);
}

/* ---------- ASCII portrait of Kevin (charset rendered from headshot) ---------- */
const KEVIN_ASCII = `                                                                
                                                                
                                                                
                               .. ...                           
                        .. ...,::,,,. ..                        
                       . ,-==+++**+++=;,,;                      
                       ,---=+***xXXXxx*+;.                      
                    . ,;;;=+**xxX#@##Xxx*=,                     
                   :,:-:,;=+xxXX#@@@@@#Xx*=:,                   
                   ;,+=,,;=+xxxX#@#@@@@Xxx*;:                   
                  .,+x+,,;=*xXXXX##@@@#XXx*=:.                  
                  :;=X+,:;=*X##@#@#@@@##Xx*;,                   
                   ,+Xx:;==+*xX######@@##X*;                    
                   ,+#+.,:...:-+xx*+=-+xxx*-.                   
                  .,,+*..   ,   -##-,. ;;==++.;.                
                  ,:x=...:=+-, -##X=;:;=;+X*;+,                 
                  :,X;;+****+:.-####XxxX##X*-*:                 
                  , =*-+XXX*-,,=#######@@#X*=+                  
                   ,-*:=*x+;.,;*#@##x=+x#XX+=:                  
                    --.;-;:.   -=*+*Xx-,=*x=;                   
                    :..,....   ,:=xXx**;,-+;                    
                    , ,=;.    .:-+==-.;-;==,                    
                     : ,=;,..:=x#@XX+xX*+=;                     
                     .. ,-,..,-+xxXxXXX*=;                      
                      :, .-;,:;--+xX#X*-;                       
                      :,. .;+=+xX###X*;:;                       
                      =,   .,-=++*x*=,,=-                       
                      ,.      ..  .,,;=+-,                      
                                  :=-=*+=;                      
                            ,,.   ,;=+*=+;                      
                           .:---::;=+*++*,                      
                             .:*=---+*=+,                       
                                  ,,::                          `;

const POST_ASCII = [
`     ┌──┐
     │ 1│ ──spawn──┐
     └──┘          │
                   ▼
              ┌──────┐
              │AGENT0│──┐
              └──────┘  │
        ┌──────┐  ┌─────┴┐ ┌──────┐
        │AGENT1│  │AGENT2│ │AGENT3│
        └──────┘  └──────┘ └──────┘
              │      │       │
              └──────┼───────┘
                     ▼
        ╔═══════════════════════╗
        ║  8 CHANNELS           ║
        ║  flat-rate            ║
        ║  one operator         ║
        ╚═══════════════════════╝`,

`            ╱╲
           ╱  ╲       ◉ kitchen.light    ok
          ╱    ╲      ◉ porch.cam        ok
         ╱      ╲     ◉ thermostat       ok
        ╱        ╲    ◉ garage.door      ok
       ╱──────────╲   ▣ utility.sensor   FIX
       │ ▣  ▣  ▣ │   ▣ basement.pump    FIX
       │          │   ▢ guest.wifi       —
       │ ▣ [▌] ▣ │   ▢ shed.cam          —
       │          │
       │ ▣  ▣  ▣ │   ──────────────────────
       └──────────┘   1119 entities · 30% off
       ════════════   ► AGENT REPAIRING ░░░`,

`  ┌─ FREE ──────────────────────────┐
  │ groq        ▰▰▰▰▰▰▰▰▰▰▰▰▰▰▰▰▰▰▰ │
  │ cerebras    ▰▰▰▰▰▰▰▰▰▰▰▰▰▰▰     │
  │ workers.ai  ▰▰▰▰▰▰▰▰▰▰▰▰        │
  │ openrouter  ▰▰▰▰▰▰▰▰▰           │
  └────────────────────────────────┘
                  │
                  ▼  escalate
  ┌─ PAID ──────────────────────────┐
  │ sonnet      ▰▰▰▰▰▰▰▰▰           │
  │ gpt-4o      ▰▰▰▰▰               │
  │ opus        ▰▰                  │
  └────────────────────────────────┘
       200+ MODELS  · $0 → $X.XX`];


/* ---------- SOCIAL ICONS ---------- */
function SocialIcons() {
  const links = [
    {
      href: "https://x.com/KZic",
      key: "X",
      handle: "@KZic",
      path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
    },
    {
      href: "https://linkedin.com/in/kzicherman",
      key: "LinkedIn",
      handle: "kzicherman",
      path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
    },
    {
      href: "https://github.com/KevinZai",
      key: "GitHub",
      handle: "KevinZai",
      path: "M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12",
    },
  ];
  return (
    <div className="social-status-grid">
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          aria-label={l.key}
          target="_blank"
          rel="noreferrer"
          className="social-card"
          style={{
            display: "block",
            border: "1px solid var(--line-strong)",
            padding: "7px 8px",
            background: "var(--bg-1)",
            color: "var(--fg-3)",
            textDecoration: "none",
            transition: "border-color 150ms ease, color 150ms ease",
            minWidth: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--amber)";
            e.currentTarget.style.color = "var(--fg)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--line-strong)";
            e.currentTarget.style.color = "var(--fg-3)";
          }}
        >
          <div style={{ fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.18em", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.key}</div>
          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 5, fontWeight: 600 }}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              style={{ flexShrink: 0 }}
            >
              <path d={l.path} />
            </svg>
            <span style={{ fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.handle}</span>
          </div>
        </a>
      ))}
      <div
        style={{
          display: "block",
          border: "1px solid var(--line-strong)",
          padding: "7px 8px",
          background: "var(--bg-1)",
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.18em", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>status</div>
        <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 4, fontWeight: 600, fontSize: 10, color: "var(--green)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          <span className="led" />ONLINE
        </div>
      </div>
    </div>
  );
}

const BIZ_LINKS = [
  { label: "x", text: "@KZic", href: "https://x.com/KZic", external: true },
  { label: "linkedin", text: "kzicherman", href: "https://linkedin.com/in/kzicherman", external: true },
  { label: "github", text: "KevinZai", href: "https://github.com/KevinZai", external: true },
  { label: "email", text: "hi@kevinz.ai", href: "mailto:hi@kevinz.ai" },
  { label: "now", text: "/now", href: "/now" },
  { label: "uses", text: "/uses", href: "/uses" },
];

function BizCard() {
  const [torontoTime, setTorontoTime] = useState("");
  const cardRef = React.useRef(null);

  useEffect(() => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Toronto",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const tick = () => setTorontoTime(formatter.format(new Date()));
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  // 3D tilt on mousemove. Disabled if prefers-reduced-motion.
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;  // 0..1
        const y = (e.clientY - r.top) / r.height;  // 0..1
        const ry = (x - 0.5) * 8;   // ±4deg horizontal
        const rx = (0.5 - y) * 6;   // ±3deg vertical
        card.style.setProperty("--tilt-x", rx + "deg");
        card.style.setProperty("--tilt-y", ry + "deg");
        const gx = Math.round(x * 100);
        const gy = Math.round(y * 100);
        card.style.setProperty("--glint-x", gx + "%");
        card.style.setProperty("--glint-y", gy + "%");
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      card.style.setProperty("--tilt-x", "0deg");
      card.style.setProperty("--tilt-y", "0deg");
    };
    card.addEventListener("mousemove", onMove);
    card.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      card.removeEventListener("mousemove", onMove);
      card.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div className="hero-card-column">
      <div className="biz-card" ref={cardRef} aria-label="Kevin Zicherman business card">
        <div className="biz-titlebar" aria-hidden="true">
          <div className="biz-tb-lights">
            <span className="r" />
            <span className="a" />
            <span className="g" />
          </div>
          <div className="biz-tb-name">~/identity · whoami</div>
          <div className="biz-tb-meta">v05.01</div>
        </div>
        <div className="biz-glint" aria-hidden="true" />
        <div className="biz-grid">
          <div className="biz-face">
            <window.AsciiPortrait
              compact
              art={KEVIN_ASCII}
              label=""
              force={74}
              radius={66}
              returnSpring={0.24}
              damping={0.74}
            />
          </div>
          <div className="biz-meta">
            <div className="name">Kevin Zicherman</div>
            <div className="role">CEO · Operator · Architect</div>
            <div className="tag">I architect AI agent teams in production.</div>
            <div className="biz-status-badge" aria-label="agents online">
              <span className="biz-live-dot" aria-hidden="true">●</span>
              <span>agents online</span>
            </div>
            <hr />
            <div className="biz-specs" style={{ marginBottom: 10, fontFamily: "var(--mono)", fontSize: "0.7rem" }}>
              {[
                ["STACK",      "claude · codex · commander"],
                ["RUNTIME",    "openclaw + hermes + cli"],
                ["BILLING",    "flat-rate subscriptions"],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", gap: 10, marginBottom: 4 }}>
                  <span style={{ color: "var(--fg-3)", letterSpacing: "0.14em", minWidth: 84, flexShrink: 0 }}>{k}</span>
                  <span style={{ color: "var(--fg)" }}>{v}</span>
                </div>
              ))}
            </div>
            <hr />
            <div className="links">
              {BIZ_LINKS.map((link) => (
                <div key={link.label} className="biz-link-item">
                  <span aria-hidden="true">→</span>
                  <a
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                  >
                    <strong>{link.label}</strong>
                    <em>{link.text}</em>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="footer">
          <span className="live"><span className="biz-live-dot" aria-hidden="true">●</span> agents online</span>
          <span className="biz-footer-meta">YYZ · {torontoTime || "--:--"} · UTC-5 · v05.01</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- TOP BAR ---------- */
function TopBar() {
  const [time, setTime] = useState("");
  const [mode, setMode] = useKzMode();
  const readSoundOn = () => kzReadStorage(KZ_SOUND_KEY) === "on";
  const [soundOn, setSoundOn] = useState(readSoundOn);
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const rawH = d.getHours();
      const ampm = rawH >= 12 ? "PM" : "AM";
      const h12 = ((rawH + 11) % 12) + 1; // 0 → 12, 13 → 1
      const h = String(h12).padStart(2, "0");
      const m = String(d.getMinutes()).padStart(2, "0");
      const s = String(d.getSeconds()).padStart(2, "0");
      setTime(`${h}:${m}:${s} ${ampm}`);
    };
    tick();const id = setInterval(tick, 1000);return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onSound = (event) => {
      const state = event.detail && event.detail.state;
      setSoundOn(state ? state === "on" : readSoundOn());
    };
    const onStorage = (event) => {
      if (event.key === KZ_SOUND_KEY) setSoundOn(event.newValue === "on");
    };
    window.addEventListener("kz:sound", onSound);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("kz:sound", onSound);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const openCmdK = () => {
    if (typeof window === "undefined") return;
    if (window.CmdK && typeof window.CmdK.open === "function") {
      window.CmdK.open();
      return;
    }
    try {
      window.dispatchEvent(new CustomEvent("kz:cmdk-open"));
    } catch (_) {}
    try {
      const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");
      window.dispatchEvent(new KeyboardEvent("keydown", {
        key: "k",
        bubbles: true,
        cancelable: true,
        metaKey: isMac,
        ctrlKey: !isMac
      }));
    } catch (_) {}
  };

  const toggleSound = () => {
    if (typeof window === "undefined") return;
    let next = soundOn ? "off" : "on";
    if (window.kzSound && typeof window.kzSound.toggle === "function") {
      next = window.kzSound.toggle();
    } else {
      kzWriteStorage(KZ_SOUND_KEY, next);
      window.dispatchEvent(new CustomEvent("kz:sound", { detail: { state: next } }));
    }
    setSoundOn(next === "on");
  };

  return (
    <div className="topbar">
      <div className="container topbar-inner">
        <a href="/" className="brand" aria-label="kevinz.ai home">
          <span className="sigil">KZ</span>
          <span>kevinz.ai</span>
        </a>
        <nav>
          <a href="#commander">commander</a>
          <a href="#projects">projects</a>
          <a href="#writing">writing</a>
          <a href="/uses">uses</a>
          <a href="#newsletter">subscribe</a>
          <a href="/about">about</a>
          <a href="#contact" className="gh-link">contact</a>
        </nav>
        <div className="status-cluster">
          <span className="dim kz-topbar-time">{time} EST</span>
          <span className="dim kz-topbar-dot">·</span>
          <span className="dim kz-topbar-loc">YYZ</span>
          <span className="kz-topbar-controls">
            <span className="kz-mode-toggle" role="group" aria-label="Audience mode">
              <button
                type="button"
                className={mode === "exec" ? "is-active" : ""}
                aria-pressed={mode === "exec"}
                onClick={() => setMode("exec")}
                title="Exec mode">
                exec
              </button>
              <button
                type="button"
                className={mode === "builder" ? "is-active" : ""}
                aria-pressed={mode === "builder"}
                onClick={() => setMode("builder")}
                title="Builder mode">
                builder
              </button>
            </span>
            <button
              type="button"
              className="kz-topbar-pill kz-cmdk-pill"
              onClick={openCmdK}
              aria-label="Open command palette"
              title="Open command palette">
              ⌘K
            </button>
            <button
              type="button"
              className={`kz-topbar-pill kz-sound-toggle ${soundOn ? "is-on" : ""}`}
              onClick={toggleSound}
              aria-label={soundOn ? "Mute terminal sound" : "Enable terminal sound"}
              aria-pressed={soundOn}
              title={soundOn ? "Sound on" : "Sound off"}>
              ♪
            </button>
          </span>
        </div>
      </div>
    </div>);

}

/* ---------- PROOF SECTION (10 LOCKED canonical claims) ---------- */
const PROOF_CLAIMS = [
  "$1M+ revenue, automated.",
  "5 companies. zero W-2 hires.",
  "AI agent teams on call. 24/7.",
  "20 years shipping. WiFi to AI.",
  "self-hosted + cloud.",
  "flat-rate subscriptions.",
  "Commander — the AI command layer (OSS).",
  "WhatsApp front desks. WiFi rails.",
  "Captive portals in 40+ countries.",
  "Claude Partner Network · OpenAI Builder · Codex shipper.",
];

function ProofSection() {
  return (
    <section id="proof" data-section="proof" className="section-pad">
      <div className="container">
        <h2 className="box-head">
          <window.AsciiBadge glyph="diamond" count={3} />
          <span>// proof — by the numbers</span>
          <span className="rule" />
        </h2>
        <div className="hero-proof-grid mt-32">
          {PROOF_CLAIMS.map((claim, i) => (
            <div key={i} className="hero-proof-cell">
              <span className="hero-proof-index">{String(i + 1).padStart(2, "0")}/10</span>
              <span className="hero-proof-text">{claim}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- VERIFIED TRUST STRIP ---------- */
const VERIFIED_PARTNERS = [
  { label: "Claude Partner Network", tip: "Claude Partner Network — member; production systems on Claude." },
  { label: "OpenAI Builder", tip: "OpenAI Builder — Codex and GPT fleet orchestration." },
  { label: "NVIDIA Inception", tip: "NVIDIA Inception — applied to the startup program; independent builder." },
  { label: "Cal.com", tip: "Cal.com — scheduling rail for operator calls." },
  { label: "Linear", tip: "Linear — issue-first build tracking." },
  { label: "Stripe", tip: "Stripe — billing and SaaS revenue rails." },
];

function VerifiedStrip() {
  return (
    <section data-section="verified" className="verified-strip" aria-label="Verified trust signals">
      <div className="container">
        <div className="verified-row">
          {VERIFIED_PARTNERS.map((item) => (
            <span key={item.label} className="verified-chip" tabIndex="0" data-tip={item.tip} title={item.tip}>
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

const EXEC_STATS = [
  { label: "agent teams", value: "live" },
  { label: "brands", value: "5", count: 5 },
];

const EXEC_EXPERIENCE = [
  { name: "Commander", role: "CEO", dates: "2026-present", metric: "OSS · MIT", color: "var(--amber)" },
  { name: "ReadyIQ", role: "Founder", dates: "2024-present", metric: "40+ clients", color: "var(--fg-3)" },
  { name: "DMHub", role: "Founder", dates: "2024-present", metric: "6 channels", color: "var(--green)" },
  { name: "MyWiFi", role: "Founder", dates: "2010-present", metric: "40+ countries", color: "var(--amber)" },
  { name: "Guest Networks", role: "Co-founder", dates: "2023-present", metric: "99.7% uptime SLA", color: "var(--fg-2)" },
  { name: "OpenClaw", role: "Operator", dates: "2025-present", metric: "agent runtime", color: "var(--amber)" },
];

function ExecCoverBanner() {
  return (
    <div className="exec-cover" aria-label="Executive profile cover">
      <div className="container exec-cover-inner">
        <div className="exec-cover-tag">EXEC MODE / v05.01</div>
      </div>
    </div>
  );
}

function ExecProfileHeader() {
  const linkGlyphs = {
    x: "X",
    linkedin: "in",
    github: "GH",
    email: "@",
    now: "now",
    uses: "uses",
  };

  return (
    <Reveal>
      <section className="exec-profile-card" aria-label="Kevin Zicherman executive profile">
        <div className="exec-profile-main">
          <div className="exec-avatar" aria-label="Kevin Zicherman portrait">
            <window.AsciiPortrait
              compact
              art={KEVIN_ASCII}
              label=""
              force={42}
              radius={54}
              returnSpring={0.22}
              damping={0.76}
            />
          </div>

          <div>
            <div className="exec-name-row">
              <h1 className="exec-name">Kevin Zicherman</h1>
              <span className="exec-badge"><span className="led" /> fleet online</span>
            </div>
            <div className="exec-headline">CEO · Operator · Architect of agentic infrastructure</div>
            <div className="exec-location">Toronto · Available for select advisory + investment</div>

            <div className="exec-stat-row" aria-label="Executive profile stats">
              {EXEC_STATS.map((stat) => (
                <span className="exec-stat-chip" key={stat.label}>
                  <strong>{stat.count ? <CountUp to={stat.count} /> : stat.value}</strong> {stat.label}
                </span>
              ))}
            </div>

            <div className="exec-cta-row">
              <a className="btn btn-primary" href="#newsletter">▸ Get The Agent Report</a>
              <a className="btn btn-ghost" href="#contact">$ Book intro call</a>
            </div>
          </div>

          <div className="exec-link-rail" aria-label="Profile links">
            {BIZ_LINKS.map((link) => (
              <a
                key={link.label}
                className="exec-link-icon"
                href={link.href}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noopener noreferrer" : undefined}
                aria-label={link.label + " · " + link.text}
                title={link.text}
              >
                {linkGlyphs[link.label] || link.label}
              </a>
            ))}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function ExecAbout() {
  return (
    <Reveal>
      <section className="exec-panel" aria-label="About Kevin Zicherman">
        <div className="exec-panel-head">
          <window.AsciiBadge glyph="diamond" count={3} />
          <span>// summary</span>
          <span className="rule" />
        </div>
        <p className="exec-about-copy">
          I run a fleet of AI agents across 5 brands. They handle outreach, support, ops, content, and dev work. I built the runtime (OpenClaw + Hermes) and the orchestrator (Commander). I'm interested in: agentic infrastructure, applied AI for SMBs, fleet operations.
        </p>
        <div className="exec-interest-row" aria-label="Interest areas">
          {["agentic infrastructure", "applied AI for SMBs", "fleet operations"].map((item) => (
            <span className="exec-interest-chip" key={item}>{item}</span>
          ))}
        </div>
      </section>
    </Reveal>
  );
}

function ExecFeatured() {
  const metricFromOutcome = (outcome) => outcome.split(" · ")[0];

  return (
    <Reveal delay={80}>
      <section className="exec-panel" aria-label="Featured case studies">
        <div className="exec-panel-head">
          <window.AsciiBadge glyph="block" count={3} />
          <span>// featured</span>
          <span className="rule" />
        </div>
        <div className="exec-featured-grid">
          {CASE_STUDIES.map((study, i) => (
            <article key={study.title} className="exec-featured-card">
              <div className="exec-feature-thumb">
                <span className="exec-feature-metric">{metricFromOutcome(study.outcome)}</span>
              </div>
              <div className="exec-feature-body">
                <div className="exec-feature-title">{study.title}</div>
                <div className="exec-feature-subhead">{study.problem}</div>
                <div className="exec-feature-stack">case-{String(i + 1).padStart(2, "0")} · {study.stack}</div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </Reveal>
  );
}

function ExecExperience() {
  return (
    <Reveal delay={120}>
      <section className="exec-panel" aria-label="Experience">
        <div className="exec-panel-head">
          <window.AsciiBadge glyph="hash" count={3} />
          <span>// experience</span>
          <span className="rule" />
        </div>
        <div className="exec-experience-list">
          {EXEC_EXPERIENCE.map((item) => (
            <article className="exec-exp-row" key={item.name}>
              <span className="exec-exp-dot" style={{ "--exec-brand-c": item.color }} aria-hidden="true" />
              <span className="exec-exp-name">{item.name}</span>
              <span className="exec-exp-role">{item.role}</span>
              <span className="exec-exp-date">{item.dates}</span>
              <span className="exec-exp-metric">{item.metric}</span>
            </article>
          ))}
        </div>
      </section>
    </Reveal>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  const [mode] = useKzMode();
  const { ready: introReady, fragNodes: introFrags } = useHeroIntro();
  return (
    <>
      {mode === "exec" && (
        <>
          <ExecCoverBanner />
          <section className="hero exec-profile-section" data-section="hero">
            <div className="container">
              <ExecProfileHeader />
              <div className="exec-profile-body">
                <main className="exec-main-stack">
                  <ExecAbout />
                </main>
                <aside className="exec-contact-rail" aria-label="Contact rail">
                  <BizCard />
                </aside>
              </div>
            </div>
          </section>
        </>
      )}

      {mode !== "exec" && (
      <section className="hero" data-section="hero">
        <div className="container">
          <div className="hero-grid">
          <div>
            <div className="prompt-line" style={{ marginBottom: 28 }}>
              <span className="ps">kevin@toronto</span>
              <span className="dim">:</span>
              <span className="amber">~/kevinz.ai</span>
              <span className="dim"> $ </span>
              <span>whoami<span className="cursor" /></span>
            </div>

            {/* HERO COPY — locked PRIMARY. Reserves staged below; see Linear CC-490. */}
            {/* RESERVE A — operational-scale flex:
                  > I run AI agent teams.
                    They run my company.                                          */}
            {/* RESERVE B — hire-AI hook:
                  > AI is a workforce.
                    I'm hiring.                                                   */}
            <HeroH1Animated ready={introReady} fragNodes={introFrags} />

            <div className="mt-32" style={{ fontSize: 14, color: "var(--fg-2)", maxWidth: "60ch", lineHeight: 1.7 }}>
              <div><span className="dim">$ cat ./bio.md</span></div>
              <div className="mt-8 hero-bio-line" style={{ color: "var(--fg-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span className="hero-bio-prefix">CEO · operator · architect of{" "}</span>
                <span className="hero-bio-typewriter"><Typewriter phrases={[
                  "Claude Code Commander",
                  "white-label WiFi marketing",
                  "no-human companies",
                  "Data layer for physical spaces",
                  "a flat-rate AI stack",
                  "the AI command layer (OSS)",
                  "AI agents for WhatsApp",
                  "The Claude Code Bible",
                  "AI agent teams in production."
                ]} /></span>
              </div>
            </div>

            {/* Inline subscribe CTA — CC-867 (Opus team-debate Growth role finding). */}
            <HeroSubscribeForm />

            {/* CTA hierarchy: primary = inline subscribe form above (the asset that compounds).
                Secondary = ghost-style, muted weight, so the eye lands on primary first. */}
            <div className="row gap-12 mt-32 hero-cta-row" style={{ alignItems: "center" }}>
              <a href="https://www.readyiq.ai/book" target="_blank" rel="noreferrer" style={{
                fontSize: 12, color: "#a78bfa", letterSpacing: "0.08em",
                textDecoration: "none", padding: "8px 4px",
                borderBottom: "1px dashed rgba(124,58,237,0.6)"
              }}>$ book an AI Audit →</a>
            </div>

            {/* 5-brands social proof — above the fold, accent-tagged so each brand
                visually previews the per-section accent applied in the deep-dives.
                Squared tags (matches Projects-table style), label on its own line. */}
            {/* Navigation, not a CTA — kept visually quieter than the scorecard link above. */}
            <div className="brand-pills-section" style={{ opacity: 0.72 }}>
              <div className="brand-pills-label">operating ▸</div>
              <div className="brand-pills-row">
                {[
                  { n: "Commander", c: "#FF6B47", href: "/products/commander" },
                  { n: "ReadyIQ", c: "#7C3AED", href: "/products/readyiq" },
                  { n: "DMHub", c: "#00DC82", href: "/products/dmhub" },
                  { n: "MyWiFi", c: "#0096FF", href: "/products/mywifi" },
                  { n: "Guest Networks", c: "#FF1E56", href: "/products/guest-networks" }
                ].map((b) => (
                  <a
                    key={b.n}
                    href={b.href}
                    className="brand-pill"
                    style={{ "--pill-c": b.c, fontWeight: 500 }}
                  >{b.n}</a>
                ))}
              </div>
            </div>
            <div className="mt-20 hero-meta-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px 20px", fontSize: 11, color: "var(--fg-3)", letterSpacing: "0.12em", textTransform: "uppercase", maxWidth: "100%" }}>
              <span><span className="amber">»</span> Claude Partner Network</span>
              <span><span className="amber">»</span> OpenAI Builder</span>
              <span><span className="amber">»</span> 20+ Years CEO</span>
              <span><span className="amber">»</span> Author · Speaker</span>
            </div>
          </div>

          <BizCard />
        </div>

        {/* partner logos — real authentic brand marks */}
        <div className="mt-40 partners-block" style={{ border: "1px solid var(--line-strong)", background: "var(--bg-1)" }}>
          <div className="partner-header-row" style={{ padding: "10px 14px", borderBottom: "1px dashed var(--line)", fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.22em", textTransform: "uppercase", display: "flex", placeContent: "space-between" }}>
            <span><span className="amber">▸</span> partners / model_providers.toml</span>
            <span className="dim partner-header-right">[ 06 frontier labs · production traffic ]</span>
          </div>
          <div className="partner-grid" style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)" }}>
            {[
            { k: "01", logo: "logos/anthropic.svg", name: "Anthropic", sub: "claude · cowork", status: "ACTIVE" },
            { k: "02", logo: "logos/claudecode.svg", name: "Claude Code", sub: "IDE · desktop", status: "ACTIVE" },
            { k: "03", logo: "logos/openai.svg", name: "OpenAI", sub: "gpt · agents", status: "ACTIVE" },
            { k: "04", logo: "logos/codex.svg", name: "Codex", sub: "oauth · computer", status: "ACTIVE" },
            { k: "05", logo: "logos/vertexai.svg", name: "Google AI", sub: "gemini · vertex", status: "ACTIVE" },
            { k: "06", logo: "logos/grok.svg", name: "xAI Grok", sub: "grok-fast", status: "ACTIVE" }].
            map((p, i, arr) =>
            <div key={p.k} style={{
              padding: "44px 14px 26px",
              borderRight: i < arr.length - 1 ? "1px solid var(--line)" : "none",
              position: "relative",
              background: "var(--bg-1)",
              minHeight: 140,
              display: "flex", flexDirection: "column", placeContent: "center"
            }}>
                <div style={{ position: "absolute", top: 12, left: 12, fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.2em" }}>{p.k}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img src={p.logo} alt={p.name} style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--fg)", lineHeight: 1.15 }}>{p.name}</span>
                </div>
                <div style={{ marginTop: 10, fontSize: 9, color: "var(--fg-3)", letterSpacing: "0.14em", textTransform: "uppercase", lineHeight: 1.4 }}>
                  {p.sub}
                </div>
              </div>
            )}
          </div>
          {/* NVIDIA Inception strip — connected directly to partners grid, shares outer border.
              Off-site build.nvidia.com CTA removed from hero (pre-conversion cold-traffic leak). */}
          <div className="nvidia-inline" style={{ borderTop: "1px solid var(--line)", padding: "10px 14px", display: "flex", alignItems: "center", placeContent: "space-between", gap: 14, fontSize: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <img src="logos/nvidia.svg" alt="NVIDIA" style={{ height: 18, width: "auto", flexShrink: 0 }} />
              <span style={{ color: "var(--fg)", fontWeight: 600 }}>Inference partner · NVIDIA Inception applicant.</span>
              <span className="dim" style={{ marginLeft: 4 }}>Built on NVIDIA's open inference stack — independent.</span>
            </div>
          </div>
        </div>

        </div>
      </section>
      )}
      {/* VerifiedStrip removed — duplicated Hero meta-grid + Partners grid + Credentials.
          Kept in exports for optional future re-mount inside exec mode. */}
    </>);

}

/* ---------- STATS ---------- */
function StatsBar() {
  const reducedMotion = usePrefersReducedMotion();
  const ref = useRef(null);
  const started = useRef(false);
  const raf = useRef(0);
  const [progress, setProgress] = useState(() => reducedMotion ? 1 : 0);
  const stats = [
  { n: 67, text: "67", l: "apps i use", suffix: "+", desc: "tools / models / infra" },
  { n: 5, text: "5", l: "brands operated", suffix: "", desc: "one operator · zero W-2" },
  { n: 48, text: "48", l: "blog posts shipped", suffix: "", desc: "operator essays · field reports" }];

  useEffect(() => {
    if (reducedMotion) {
      started.current = true;
      setProgress(1);
      return;
    }
    const node = ref.current;
    if (!node) return;
    let observer = null;
    const start = () => {
      if (started.current) return;
      started.current = true;
      const t0 = performance.now();
      const duration = 2000;
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        const eased = easeOutExpo(t);
        setProgress(eased);
        if (t < 1) {
          raf.current = requestAnimationFrame(tick);
        } else {
          setProgress(1);
          flashAgentCountTile();
        }
      };
      raf.current = requestAnimationFrame(tick);
    };
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          start();
          if (observer) observer.disconnect();
        }
      });
    }, { threshold: 0.35, rootMargin: "0px 0px -60px 0px" });
    observer.observe(node);
    return () => {
      if (observer) observer.disconnect();
      cancelAnimationFrame(raf.current);
    };
  }, [reducedMotion]);

  return (
    <section data-section="stats" style={{ padding: "20px 0 60px" }} ref={ref}>
      <div className="container">
        <Reveal>
          <div className="stats-grid">
            {stats.map((s, i) =>
            <div className="stat-cell" key={i}>
                <div className="label">{s.l}</div>
                <div className="desc">{s.desc}</div>
                <div className="bars">
                  <Heatmap
                    pattern={heatmapTextPattern(s.text, 40, 7, progress)}
                    cols={40}
                    rows={7}
                    size="sm"
                    alive={false}
                    className="spells-number"
                  />
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>);

}

/* ---------- TOOLCHAIN ---------- */
function Toolchain() {
  const [mode] = useKzMode();
  if (mode === "exec") return null;

  // Real logos in /logos/ folder. Tagged "have" = user provided the SVG; "suggest" = recommended addition.
  // Brands without an SVG asset render as a clean text-only chip (no fake icon — placeholders are honest).
  const groups = [
  {
    label: "Models · routed in production",
    tag: "[ inference ]",
    items: [
    { name: "Claude", logo: "logos/claude.svg", note: "primary reasoning", have: true },
    { name: "Gemma", logo: "logos/gemma.svg", note: "local / air-gapped", have: true },
    { name: "Mistral", logo: "logos/mistral.svg", note: "codestral · code spec", have: true },
    { name: "HuggingFace", logo: "logos/huggingface.svg", note: "121-model fallback", have: true },
    { name: "Nous Research", logo: "logos/nousresearch.svg", note: "open-weights research", have: true },
    { name: "Perplexity", logo: "logos/perplexity.svg", note: "research · grounded", have: true },
    { name: "Ollama", logo: "logos/ollama.svg", note: "local model runtime", have: true },
    { name: "Groq", logo: "logos/groq.svg", note: "22 bulk agents · llama", have: true },
    { name: "Cerebras", logo: "logos/cerebras.svg", note: "qwen · gpt-oss", have: true },
    { name: "Cloudflare AI", logo: "logos/cloudflare.svg", note: "edge inference · 25 models", have: true },
    { name: "OpenRouter", logo: "logos/openrouter.svg", note: "29-model fallback router", have: true },
    { name: "MiniMax", logo: "logos/minimax.svg", note: "long-context · multimodal", have: true },
    { name: "Portkey", note: "llm gateway" },
    { name: "Plausible", logo: "logos/plausibleanalytics.svg", note: "privacy-first analytics", have: true }]

  },
  {
    label: "Agents · build & dispatch",
    tag: "[ orchestration ]",
    items: [
    { name: "OpenClaw", logo: "logos/openclaw.svg", note: "self-hosted gateway", have: true },
    { name: "Claude Code", logo: "logos/claudecode.svg", note: "primary IDE", have: true },
    { name: "Codex CLI", logo: "logos/codex.svg", note: "oauth-only · never api key", have: true },
    { name: "Cursor", logo: "logos/cursor.svg", note: "rapid prototyping", have: true },
    { name: "Commander", note: "my own · 70 plugin skills" },
    { name: "Paperclip", note: "issue-first dispatch" },
    { name: "n8n", logo: "logos/n8n.svg", note: "self-hosted workflows", have: true },
    { name: "Langfuse", logo: "logos/langfuse.svg", note: "llm tracing · cost", have: true },
    { name: "Promptfoo", note: "eval framework · regression" },
    { name: "Datadog", logo: "logos/datadog.svg", note: "obs · alt to langfuse", have: true },
    { name: "OpenWebUI", logo: "logos/openwebui.svg", note: "local model UI", have: true },
    { name: "Copilot Studio", logo: "logos/copilot.svg", note: "microsoft · enterprise agents", have: true },
    { name: "Inngest", note: "durable workflows" },
    { name: "Logsnag", note: "event tracking · alerts" }]

  },
  {
    label: "Infra · where it runs",
    tag: "[ self-hosted ]",
    items: [
    { name: "AWS", logo: "logos/aws.svg", note: "infra · iam · primary cloud", have: true },
    { name: "Vercel", logo: "logos/vercel.svg", note: "frontend · next.js", have: true },
    { name: "Railway", logo: "logos/railway.svg", note: "backend services", have: true },
    { name: "Cloudflare", logo: "logos/cloudflare.svg", note: "tunnel · workers · cdn", have: true },
    { name: "Tailscale", logo: "logos/tailscale.svg", note: "sovereign mesh · zero-trust", have: true },
    { name: "Coolify", note: "self-hosted deploys" },
    { name: "Docker", logo: "logos/docker.svg", note: "containers · everywhere", have: true },
    { name: "BigQuery", logo: "logos/bigquery.svg", note: "vertex grounding · analytics", have: true },
    { name: "Supabase", logo: "logos/supabase.svg", note: "db + auth · postgres", have: true },
    { name: "Fly.io", logo: "logos/flydotio.svg", note: "backend region failover", have: true },
    { name: "OrbStack", note: "docker on macOS" },
    { name: "PM2", logo: "logos/pm2.svg", note: "20+ persistent services", have: true },
    { name: "Caddy", logo: "logos/caddy.svg", note: "reverse proxy", have: true },
    { name: "Redis", logo: "logos/redis.svg", note: "cache + job queue", have: true },
    { name: "GitHub", logo: "logos/github.svg", note: "source · ci · 31 repos", have: true },
    { name: "Sentry", logo: "logos/sentry.svg", note: "error tracking", have: true },
    { name: "Stripe", logo: "logos/stripe.svg", note: "billing · saas revenue", have: true },
    { name: "Resend", logo: "logos/resend.svg", note: "transactional email", have: true },
    { name: "PostHog", logo: "logos/posthog.svg", note: "product analytics", have: true },
    { name: "Twilio", logo: "logos/twilio.svg", note: "sms · voice agents", have: true },
    { name: "1Password", logo: "logos/1password.svg", note: "auth · op:// refs", have: true }]

  },
  {
    label: "Knowledge · think & write",
    tag: "[ workflow ]",
    items: [
    { name: "Obsidian", logo: "logos/obsidian.svg", note: "second brain · vault sync", have: true },
    { name: "Notion", logo: "logos/notion.svg", note: "specs · client docs", have: true },
    { name: "Slack", logo: "logos/slack.svg", note: "team comms · agent alerts", have: true },
    { name: "Discord", logo: "logos/discord.svg", note: "community · dev forums", have: true },
    { name: "Todoist", logo: "logos/todoist.svg", note: "personal tasks · agent inbox", have: true },
    { name: "Home Assistant", logo: "logos/homeassistant.svg", note: "local automations · mqtt", have: true },
    { name: "Linear", logo: "logos/linear.svg", note: "personal dev tasks · CC-", have: true },
    { name: "Attio", note: "crm · lead scoring" },
    { name: "Clay", note: "data enrichment · gtm ops" },
    { name: "HubSpot", logo: "logos/hubspot.svg", note: "breeze · marketing ops", have: true },
    { name: "Salesforce", logo: "logos/salesforce.svg", note: "agentforce · enterprise", have: true },
    { name: "Granola", note: "meeting → agent ingestion" },
    { name: "BlueBubbles", note: "imessage agent bridge" },
    { name: "Raycast", logo: "logos/raycast.svg", note: "launcher · ai snippets", have: true }]

  }];


  return (
    <section id="toolchain" data-section="toolchain" style={{ padding: "60px 0 40px" }}>
      <div className="container">
        <div className="box-head">
          <window.AsciiBadge glyph="hash" count={3} />
          <span>// 13 — toolchain</span>
          <span className="rule" />
        </div>
        <Reveal>
          {/* terminal-style header to match the rest of the site */}
          <div style={{ borderTop: "1px solid var(--line-strong)", borderBottom: "1px solid var(--line)" }}>
            <div style={{ padding: "10px 0", borderBottom: "1px dashed var(--line)", fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.22em", textTransform: "uppercase", display: "flex", placeContent: "space-between", alignItems: "center" }}>
              <span><span className="amber">▸</span> tools / kevin@toronto.toolchain</span>
              <span className="dim">[ 04 categories · {groups.reduce((n, g) => n + g.items.length, 0)} entries ]</span>
            </div>

            <div style={{ padding: "32px 0 8px" }}>
              <div className="prompt-line" style={{ marginBottom: 14 }}>
                <span className="dim">$ </span>
                <span>cat ./uses.md<span className="cursor" /></span>
              </div>
              <h2 className="h-section" style={{ margin: 0 }}>
                tools i use.
                <br />
                <span className="dim2" style={{ fontWeight: 600 }}>
                  {">"} every provider, every dispatcher, every routing rule.
                </span>
              </h2>
              <div className="mt-16" style={{ fontSize: 13, color: "var(--fg-3)", maxWidth: "62ch", lineHeight: 1.7, width: "min(448px, 100%)" }}>
                The actual stack — <span className="amber">not the marketing version</span>. If you're trying to figure out what's real vs hype, start here.
              </div>
            </div>
          </div>

          {/* groups */}
          {groups.map((g, gi) =>
          <div key={g.label} style={{ borderBottom: "1px solid var(--line)" }}>
              <div style={{
              display: "flex", placeContent: "space-between", alignItems: "center",
              padding: "14px 0",
              borderBottom: "1px dashed var(--line)",
              fontSize: 11, color: "var(--fg-3)", letterSpacing: "0.18em", textTransform: "uppercase"
            }}>
                <span><span className="amber">{`0${gi + 1}`}</span>&nbsp;&nbsp;{g.label}</span>
                <span className="dim toolchain-group-tag">{g.tag} · {g.items.length}</span>
              </div>

              <div className="toolchain-items-grid" style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              borderLeft: "1px solid var(--line)"
            }}>
                {g.items.map((t, i) =>
              <div key={t.name} style={{
                borderRight: "1px solid var(--line)",
                borderBottom: "1px solid var(--line)",
                padding: "18px 18px 16px",
                background: "var(--bg-1)",
                display: "flex", alignItems: "flex-start", gap: 12,
                minHeight: 76,
                transition: "background 0.15s ease"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-2, #1a1a1a)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "var(--bg-1)"}>
                
                    <div style={{
                  width: 32, height: 32, flexShrink: 0,
                  display: "flex", alignItems: "center", placeContent: "center",
                  border: "1px solid var(--line)",
                  background: "var(--bg)",
                  borderRadius: 4
                }}>
                      {t.logo ?
                  <img src={t.logo} alt={t.name} style={{ width: 20, height: 20, objectFit: "contain" }} /> :

                  <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--fg-4)", letterSpacing: "-0.02em" }}>
                          {t.name.slice(0, 2).toLowerCase()}
                        </span>
                  }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                    fontSize: 13, fontWeight: 600, color: "var(--fg)",
                    letterSpacing: "-0.005em",
                    display: "flex", alignItems: "center", gap: 6
                  }}>
                        {t.name}
                        {!t.have && <span className="amber" style={{ fontSize: 10, opacity: 0.7 }} title="Suggested addition / no logo on file">*</span>}
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--fg-4)", letterSpacing: "0.06em", marginTop: 4, lineHeight: 1.4 }}>
                        {t.note}
                      </div>
                    </div>
                  </div>
              )}
              </div>
            </div>
          )}

          {/* footer note */}
          <div style={{
            padding: "16px 0",
            fontSize: 11, color: "var(--fg-4)", letterSpacing: "0.16em", textTransform: "uppercase",
            display: "flex", placeContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12
          }}>
            <span><span className="amber">▸</span> last updated · 2026.04 · live stack</span>
            <span className="dim">→ <a href="#consulting" style={{ color: "var(--amber)", textDecoration: "none" }}>build something like this →</a></span>
          </div>
        </Reveal>
      </div>
    </section>);

}

/* ---------- NEWSLETTER ---------- */
// TODO 2026-04-29: set to true once Stripe/Lemon Squeezy paywall is wired
const PAYWALL_LIVE = false;

function Newsletter() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/newsletter-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "newsletter" }),
      });
      if (res.ok) {
        setDone(true);
        window.plausible?.("email_signup", { props: { audience: "kevinzai", source: "newsletter" } });
      } else {
        const data = await res.json().catch(() => ({}));
        setErr(data.error === "invalid_email" ? "invalid email" : "something went wrong — try again");
      }
    } catch (_) {
      setErr("network error — try again");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="newsletter" data-section="newsletter" className="section-pad">
      <div className="container">
        <Reveal>
          <div className="newsletter">
            <div className="win-title">
              <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
              <span>~/the-agent-report</span>
              <span className="meta">SUNDAY · 08:00 EST</span>
            </div>
            <div className="body">
              <div className="box-head">
                <span>// 11 — newsletter</span>
                <span className="rule" />
              </div>
              <h2 className="h-section" style={{ maxWidth: "22ch" }}>running real businesses on AI agents. what works, what breaks, what it costs.</h2>
              <p className="lede mt-20">
                Real builds. Real cost data. Zero fluff. The build, the stack, the number, the workflow — every Sunday at 8 AM ET.
              </p>

              <div className="newsletter-preview" aria-label="Last newsletter issue preview">
                <div className="newsletter-preview-head">
                  <span>$ cat /var/mail/issue-043.md</span>
                  <span>last sent</span>
                </div>
                <div className="newsletter-preview-body">
                  <div className="newsletter-preview-title">Cost-Optimized LLM Routing</div>
                  <pre className="newsletter-preview-lines">{`Decision tree across 25+ LLM providers.
Real cost data from production agent work.
Why I route 200+ models across a flat-rate subscription stack.`}</pre>
                  <a href="/posts">▸ read full issue</a>
                </div>
              </div>

              <form className="news-row" onSubmit={handleSubmit}>
                <input
                  className="news-input"
                  type="email"
                  placeholder="you@operator.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={done || loading}
                  aria-label="Email address for The Agent Report" />

                <button className="btn btn-primary" type="submit" disabled={done || loading}>
                  {done ? "✓ subscribed" : loading ? "..." : "subscribe →"}
                </button>
              </form>
              {err && <p className="dim mt-8" style={{ fontSize: 11, color: "var(--red, #f87171)" }}>{err}</p>}

              <p className="dim mt-16" style={{ fontSize: 11, letterSpacing: "0.12em" }}>
                ▸ for operators and founders running real businesses · unsubscribe in one click
              </p>
              <div className="mt-16" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                <ThinkingBadge />
              </div>

              {PAYWALL_LIVE && (
                <div className="tier-grid">
                  <div className="tier">
                    <div className="name">free</div>
                                        <div className="desc">Weekly newsletter, full blog access, Commander OSS.</div>
                  </div>
                  <div className="tier featured">
                    <div className="name">pro · recommended</div>
                                        <div className="desc">Premium deep-dives, prompts library, monthly Q&A, members posts.</div>
                  </div>
                  <div className="tier">
                    <div className="name">insider</div>
                                        <div className="desc">Everything in Pro + Commander Pro unlock + private Discord + 1:1 monthly.</div>
                  </div>
                </div>
              )}
            </div>
            <div className="win-foot">
              <span>STATUS · LIVE</span>
              <span>OPERATORS · FOUNDERS</span>
              <span>EOL</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>);

}

/* ---------- COMMANDER ---------- */
/* Reusable "thinking with max effort" badge — orange sparkle + white text.
   Used in Footer + Newsletter section + StickyNewsletter dock. */
function ThinkingBadge({ text = "thinking with max effort", style }) {
  return (
    <span style={{ color: "var(--fg)", display: "inline-flex", alignItems: "center", gap: 8, ...style }}>
      <svg className="kz-think" viewBox="-12 -12 24 24" width="14" height="14" aria-hidden="true" style={{ flex: "none" }}>
        <g className="kz-think-rays" stroke="#F5A623" strokeWidth="1.6" strokeLinecap="round">
          <line x1="0" y1="-4.5" x2="0" y2="-10" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(45)" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(90)" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(135)" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(180)" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(225)" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(270)" />
          <line x1="0" y1="-4.5" x2="0" y2="-10" transform="rotate(315)" />
        </g>
        <circle className="kz-think-core" cx="0" cy="0" r="2.6" fill="#F5A623" />
      </svg>
      <span>{text}</span>
    </span>
  );
}

function Commander({ activeProject }) {
  const [mode] = useKzMode();
  if (mode === "exec") return null;
  if (activeProject !== "all" && activeProject !== "commander") return null;
  const chapterMarker = activeProject === "all"
    ? <window.AsciiRule num="03" label="commander" />
    : null;
  const features = [
  { g: "[01]", t: "Click-First, Not Typing", d: "13 /ccc-* workflows. Pick a button, skip the prompt engineering." },
  { g: "[02]", t: "Every Mode for Every Job", d: "YOLO autonomous build · Spec → Plan → Ship · Dialectic review · Audit · Deploy. Pick the mode." },
  { g: "[03]", t: "One Install, 457+ Skills", d: "Plugin Marketplace native. 22 agents, 23 hooks, 2 bundled MCPs. 16 more opt-in via /ccc-connect." },
  { g: "[04]", t: "Universal AI IDE", d: "Claude Code Desktop (primary). CLI, Cursor, Windsurf, Cline, Continue, Codex via MCP." }];

  return (
    <>
      {chapterMarker}
      <section id="commander" data-section="commander" className="section-pad">
        <div className="container">
          <div className="box-head">
            <window.AsciiBadge glyph="diamond" count={3} />
          <span>// 03 — featured · open source · what it does</span>
          <span className="rule" />
        </div>

        <Reveal>
          <div className="cmd-grid">
            <div>
              <a href="https://commanderplugin.com" target="_blank" rel="noreferrer" aria-label="CC Commander — commanderplugin.com" style={{ textDecoration: "none", display: "inline-block" }}>
                <pre aria-hidden="true" style={{ fontFamily: "var(--mono)", fontSize: 9, lineHeight: 1.08, color: "var(--amber)", textShadow: "0 0 12px color-mix(in oklch, var(--amber) 35%, transparent)", margin: "0 0 6px", whiteSpace: "pre" }}>{` ██████╗ ██████╗ ██████╗
██╔════╝██╔════╝██╔════╝
██║     ██║     ██║
██║     ██║     ██║
╚██████╗╚██████╗╚██████╗
 ╚═════╝ ╚═════╝ ╚═════╝`}</pre>
                <div style={{ fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.5em", color: "var(--fg-3)", textTransform: "uppercase", marginBottom: 16 }}>C O M M A N D E R</div>
              </a>
              <h2 className="h-section">
                Commander.<br />
                <span className="dim2" style={{ fontWeight: 600 }}>{">"} guided AI PM to master Claude Code instantly.</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "52ch" }}>
                One install. <span className="amber">70 plugin skills</span>. 13 click-first <span className="amber">/ccc-*</span> workflows. Core free forever.
              </p>

              <div className="row gap-12 mt-24">
                <a className="btn btn-primary" href="https://commanderplugin.com" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  ↗ commanderplugin.com
                </a>
                <a className="btn" href="https://commanderplugin.com" target="_blank" rel="noreferrer">
                  $ read the docs →
                </a>
                <a className="btn btn-ghost" href="https://github.com/KevinZai/commander" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <window.IconGithub size={14} /> github
                </a>
              </div>

              <div className="dim mt-12" style={{ fontSize: 11, color: "var(--fg-3)", letterSpacing: "0.04em", lineHeight: 1.55 }}>
                ▸ works with <span style={{ color: "var(--fg)" }}>Claude Code (CLI/Desktop)</span>, <span style={{ color: "var(--fg)" }}>Codex</span>, <span style={{ color: "var(--fg)" }}>Cursor</span> — and any IDE via <span style={{ color: "var(--fg)" }}>MCP</span>.
              </div>

              <div className="feat-list mt-32">
                {features.map((f, i) =>
                <div className="feat-row" key={i}>
                    <span className="glyph">{f.g}</span>
                    <div>
                      <h3>{f.t}</h3>
                      <p>{f.d}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Reveal delay={120}>
              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                <img
                  src="logos/ccc-hero.svg"
                  alt="CC Commander"
                  style={{ display: "block", width: "100%", maxWidth: "100%", height: "auto", margin: 0 }}
                />
                <div className="terminal">
                <div className="win-title">
                  <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
                  <span>~/projects · commander</span>
                  <span className="meta">PID 38421</span>
                </div>
                <div className="body">
                  <div><span className="term-prompt">$</span> npx commander</div>
                  <div><span className="term-ok">✓</span> Commander <span className="term-cyan">v6.3.0</span></div>
                  <div><span className="term-ok">✓</span> Detected: <span className="term-cyan">Claude Code v2.1.119</span></div>
                  <div><span className="term-ok">✓</span> Loaded <span className="term-amber">70 plugin skills</span></div>
                  <div><span className="term-ok">✓</span> <span className="term-cyan">22 agents</span> standing by</div>
                  <div><span className="term-ok">✓</span> Vendors: <span className="term-magenta">gstack</span>, <span className="term-magenta">superpowers</span>, <span className="term-magenta">CE</span> <span className="dim">+15 more</span></div>
                  <div style={{ height: 8 }} />
                  <div><span className="term-amber">commander</span> <span className="term-prompt">›</span> What are we building today?</div>
                  <div className="mt-8"><span className="term-prompt">▌</span> ship the kevinz.ai theme<span className="cursor" /></div>
                  <div className="mt-16 dim" style={{ fontSize: 11, letterSpacing: 0 }}>
                    ┌──── status ─────────────────────────┐
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", fontSize: 12, padding: "0 2px" }}>
                    <span><span className="dim">│ skills</span>     <span className="amber">457+</span></span>
                    <span><span className="dim">queue</span>       <span>0 pending</span> <span className="dim">│</span></span>
                  </div>
                  <div className="dim" style={{ fontSize: 11, letterSpacing: 0 }}>
                    └─────────────────────────────────────┘
                  </div>
                </div>
                </div>
              </div>
            </Reveal>
          </div>
        </Reveal>

        {/* ── How it works ─────────────────────────────────── */}
        <window.Reveal delay={60}>
          <div className="deepdive-how" style={{ marginTop: 52 }}>
            <div className="deepdive-how-label">── how it works ───────────────────────────────</div>
            <div className="deepdive-flow-caption">┌─ you type ────── it works ────── you ship ─┐</div>
            <pre className="deepdive-ascii-flow">{`
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  ONE COMMAND        │     │  AUTO-DETECT         │     │  READY TO WORK       │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  npx commander      │────▶│  Reads package.json  │────▶│  70 skills loaded    │
  │  Single command     │     │  Detects stack/IDE   │     │  22 agents standing  │
  │  Zero config        │     │  Picks right profile │     │  23 hooks wired      │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  YOU ASK            │     │  IT PLANS            │     │  AGENTS BUILD        │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  /ccc → menu        │────▶│  Spec interview ×5Q  │────▶│  Sonnet subagent     │
  │  /ccc-build         │     │  Plan generated      │     │  Parallel dispatch   │
  │  /ccc-review        │     │  Task breakdown      │     │  Progress reported   │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  YOU SHIP           │     │  IT GUARDS           │     │  IT LEARNS           │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  /ccc-ship          │────▶│  Tests · types · e2e │────▶│  Lessons captured    │
  │  Pre-flight checks  │     │  Security audit      │     │  CLAUDE.md updated   │
  │  Semver bump        │     │  Deploy gate         │     │  Skills compounded   │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯`}
            </pre>
            <div className="deepdive-value"><span className="vk">▸ why it matters</span> — one command turns Claude Code into a staffed dev team: 70 skills, 22 agents, zero config.</div>
          </div>
        </window.Reveal>

        {/* ── Longform ─────────────────────────────────────── */}
        <window.Reveal delay={80}>
          <div className="deepdive-longform" style={{ marginTop: 40 }}>
            <p>
              Commander grew from a specific, repeating frustration: every Claude Code session started from scratch. No context from the last session, no consistent workflows, no way to click instead of type a 200-word prompt. The first version was a single CLAUDE.md with a dozen rules. It kept getting longer. By the time it covered 15 distinct workflows, it became clear the pattern was a product, not a config file. Commander is the distillation of how a senior developer would run Claude Code if they had six months to tune it.
            </p>
            <p>
              A solo founder building a B2B SaaS in April 2026 installed Commander on day one. By the end of the week they had run the spec interview, generated a full PRD, built the auth layer with TDD, and shipped to Vercel — without writing a single raw Claude prompt. The /ccc-build workflow handled the spec questions, delegated implementation to a Sonnet subagent, and ran the verification gate. Total wall-clock time from idea to deployed feature: 4 hours. Same task from a cold Claude Code session with no tooling: estimated 2 days.
            </p>
            <p>
              The next 12 months focus on the Plugin Marketplace: third-party skill packs that install with one command and integrate cleanly with the existing 17-agent roster. Agent versioning and skill provenance tracking are in active development — so teams can lock to a specific skill version, audit what changed, and roll back safely. The OSS core stays MIT-licensed; licensing and pricing details live at <a href="https://commanderplugin.com">commanderplugin.com</a>.
            </p>
          </div>
        </window.Reveal>

        {/* ── KPI strip ────────────────────────────────────── */}
        <window.Reveal delay={100}>
          <div style={{ marginTop: 36 }}>
            <window.ProjectKpiStrip kpis={[
              { label: "skills",          value: "457+" },
              { label: "agents",  value: "managed" },
              { label: "pricing",  value: "commanderplugin.com" },
              { label: "license",         value: "OSS · MIT" }
            ]} />
          </div>
        </window.Reveal>
      </div>
    </section>
    </>
  );

}

/* ---------- PROJECTS (table style) ---------- */
function Projects() {
  const [mode] = useKzMode();
  if (mode === "exec") return null;

  // Vercel-flow palette: blue / red / amber / green / teal / violet
  const items = [
  { tag: "OSS · CLI", color: "#FF6B47", name: "Commander", desc: "The AI command layer. 70 plugin skills. Multi-agent runtime.", link: "commanderplugin.com", anchor: "#commander" },
  { tag: "Consulting", color: "#7C3AED", name: "ReadyIQ", desc: "AI deployment for non-technical CEOs. 30 days.", link: "readyiq.ai", anchor: "#readyiq" },
  { tag: "SaaS · WhatsApp", color: "#00DC82", name: "DMHub", desc: "AI front desk on WhatsApp. Pricing at dmhub.ai.", link: "dmhub.ai", anchor: "#dmhub" },
  { tag: "WiFi Marketing", color: "#0096FF", name: "MyWiFi Networks", desc: "Hospitality WiFi marketing. 40+ countries since 2010.", link: "mywifinetworks.com", anchor: "#mywifi" },
  { tag: "Physical Spaces", color: "#FF1E56", name: "Guest Networks", desc: "Query your building in plain English.", link: "guestnetworks.com", anchor: "#guestnetworks" },
  { tag: "Agentic Workforce", color: "#F5A623", name: "OpenClaw + Hermes", desc: "Self-hosted agent workforce.", link: "github.com/KevinZai/openclaw-automation", anchor: "#openclaw" }];

  return (
    <section id="projects" data-section="projects" className="section-pad">
      <div className="container">
        <div className="box-head">
          <window.AsciiBadge glyph="block" count={3} />
          <span>// 02 — the stack</span>
          <span className="rule" />
        </div>

        <Reveal>
          <div style={{ display: "flex", placeContent: "space-between", alignItems: "flex-end", marginBottom: 28, gap: 16, flexWrap: "wrap" }}>
            <div>
              <h2 className="h-section">5 brands. AI agent teams. one operator.</h2>
              <p className="dim2 mt-12" style={{ fontSize: 14, maxWidth: 540 }}>
                Each linked out to its own home. kevinz.ai is the hub.
              </p>
            </div>
            <span className="dim" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              [{items.length}] entries · sorted by reach
            </span>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="proj-table">
            {items.map((it, i) =>
            <a className="proj-row" key={i} href={it.anchor || ("https://" + it.link)}>
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                <span className="tag" style={{ "--tag-c": it.color }}>{it.tag}</span>
                <span className="proj-name">{it.name}</span>
                <span className="proj-desc">
                  {it.desc}
                  <br />
                  <span className="dim" style={{ fontSize: 11 }}>↓ jump to deep-dive · ↗ {it.link}</span>
                </span>
                <span className="arrow">↓</span>
              </a>
            )}
          </div>
        </Reveal>
      </div>
    </section>);

}

/* ---------- WRITING ---------- */
/* ---------- Decode-text hook (WR-02) ---------- */
const DECODE_RAMP = "@#%&*+=~-.:^?!|<>";
const DECODE_DURATION = 300; // ms total
const DECODE_TICK = 30;      // ms per frame

function useDecodeText(text) {
  const [displayed, setDisplayed] = useState(text);
  const rafRef = useRef(null);
  const activeRef = useRef(false);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function start() {
    if (prefersReducedMotion) return;
    activeRef.current = true;
    const chars = text.split("");
    const steps = Math.ceil(DECODE_DURATION / DECODE_TICK);
    let step = 0;

    function tick() {
      if (!activeRef.current) return;
      step++;
      const resolved = Math.floor((step / steps) * chars.length);
      const next = chars.map((ch, i) => {
        if (i < resolved) return ch;
        if (ch === " ") return " ";
        return DECODE_RAMP[Math.floor(Math.random() * DECODE_RAMP.length)];
      }).join("");
      setDisplayed(next);
      if (step < steps) {
        rafRef.current = setTimeout(tick, DECODE_TICK);
      } else {
        setDisplayed(text);
        activeRef.current = false;
      }
    }
    tick();
  }

  function cancel() {
    activeRef.current = false;
    if (rafRef.current) { clearTimeout(rafRef.current); rafRef.current = null; }
    setDisplayed(text);
  }

  useEffect(() => () => { activeRef.current = false; if (rafRef.current) clearTimeout(rafRef.current); }, []);

  return { displayed, start, cancel };
}

/* ---------- Post card with decode-title hover ---------- */
function PostCard({ p }) {
  const { displayed, start, cancel } = useDecodeText(p.title);
  const isDecoding = displayed !== p.title;

  return (
    <article
      className="post-card"
      onMouseEnter={start}
      onMouseLeave={cancel}
    >
      <a href={`/post/${p.slug}`} style={{ display: "block", textDecoration: "none", color: "inherit" }}>
        <div className="post-cover">
          <window.AsciiCover title={p.title} tag={p.cat.slice(0, 2)} seed={p.seed ?? 0} art={p.art} />
        </div>
        <div className="post-meta-row">
          <span className="cat">{p.cat}</span>
          <span className="dot">·</span>
          <span>{p.date}</span>
          <span className="dot">·</span>
          <span>{p.read}</span>
        </div>
        <div className="post-body-x">
          <h3 className="post-title" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
            <span style={{ color: isDecoding ? "var(--accent-pop, #5FBFB9)" : "var(--fg)" }}>
              {displayed}
            </span>
          </h3>
          <p className="post-excerpt">{p.excerpt}</p>
        </div>
      </a>
    </article>
  );
}

function Writing() {
  const [mode] = useKzMode();
  if (mode === "exec") return null;

  // Featured 3: newest Build Diary (open-sourced scripts) + flagship operator story + thematic anchor.
  // Other recent posts (will-ai-replace-my-job, from-one-agent-to-a-fleet, ai-agent-templates-160,
  // multi-agent-systems-beginners, write-better-ai-prompts) are surfaced via /posts and internal links.
  const posts = [
  { cat: "Operator Manifesto", date: "Apr 26 · 2026", read: "9 min", slug: "the-10-commandments-of-ai-infrastructure", title: "The 10 Commandments of AI Infrastructure", excerpt: "10 rules extracted from real failures running AI agents 24/7 across 5 companies.", art: POST_ASCII[0] },
  { cat: "Field Report", date: "Apr 17 · 2026", read: "8 min", slug: "letting-claude-code-run-overnight", title: "Letting Claude Code Run Overnight", excerpt: "The setup, checkpoints, and failure modes for running unsupervised 8-hour agent sessions.", art: POST_ASCII[1] },
  { cat: "Provider Economics", date: "May 01 · 2026", read: "18 min", slug: "cost-optimized-llm-routing", title: "Cost-Optimized LLM Routing", excerpt: "Decision tree across 25+ providers — real cost data on routing 80% of fleet work to free-tier models.", art: POST_ASCII[2] }];

  const tutorials = [
  "[T01] getting started with claude code",
  "[T02] cowork plugin · setup & power moves",
  "[T03] commander 101 · your first agent",
  "[T04] the multi-agent architecture",
  "[T05] codex + claude · multi-model workflows",
  "[T06] claudeswap · multi-MAX routing"];

  return (
    <section id="writing" data-section="writing" className="section-pad">
      <div className="container">
        <div className="box-head">
          <window.AsciiBadge glyph="pixel" count={3} />
          <span>// 09 — writing</span>
          <span className="rule" />
        </div>

        <Reveal>
          <div style={{ display: "flex", placeContent: "space-between", alignItems: "flex-end", marginBottom: 32, gap: 16, flexWrap: "wrap" }}>
            <div>
              <h2 className="h-section">build diaries & playbooks.</h2>
              <p className="lede mt-12">
                The operator's guide to AI. No predictions. No hot takes. Working code and real cost data only.
              </p>
            </div>
            <a className="btn btn-ghost" href="/posts">$ ls -all posts/ →</a>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="writing-grid">
            {posts.map((p, i) =>
            <PostCard key={i} p={{ ...p, art: p.art, seed: i * 7 + 3 }} />
            )}
          </div>
        </Reveal>

        <Reveal delay={180}>
          <div style={{ display: "flex", placeContent: "flex-end", marginTop: 24 }}>
            <a className="btn btn-ghost" href="/posts">▸ all posts</a>
          </div>
        </Reveal>

        {/* TODO 2026-04-29: tutorial grid hidden — T01-T06 posts not yet written.
            Wire each href to /post/<slug> when tutorials exist.
            T01 getting-started-claude-code, T02 cowork-plugin-setup,
            T03 commander-101, T04 multi-agent-architecture,
            T05 codex-claude-multimodel, T06 claudeswap-multi-max */}
      </div>
    </section>);

}

/* ---------- CREDS ---------- */
/* Collision modal (CC-489 / A6) — YouTube embed for the 2019 talk. */
function VideoModal({ open, onClose, videoId, meta }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 200,
      display: "flex", alignItems: "center", placeContent: "center", padding: 40
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(960px, 100%)", aspectRatio: "16/9", position: "relative",
        border: "1px solid var(--amber)", background: "#000"
      }}>
        <button onClick={onClose} aria-label="close" style={{
          position: "absolute", top: -32, right: 0, fontSize: 11,
          color: "var(--amber)", letterSpacing: "0.16em", textTransform: "uppercase",
          background: "none", border: "none", cursor: "pointer"
        }}>× close · esc</button>
        <div style={{
          position: "absolute", top: -32, left: 0, fontSize: 11,
          color: "var(--fg-3)", letterSpacing: "0.10em"
        }}>{meta}</div>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          allow="autoplay; fullscreen" allowFullScreen
          title="Collision 2019 — Kevin Zicherman"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      </div>
    </div>
  );
}

function Credentials() {
  const [collisionOpen, setCollisionOpen] = useState(false);
  const items = [
  { l: "tenure", t: "20+ Years in Tech", s: "Serial founder · Bootstrapped" },
  { l: "partner", t: "Anthropic", s: "Production systems on Claude" },
  { l: "builder", t: "OpenAI", s: "Codex · GPT fleet orchestration" },
  { l: "press", t: "Best-Selling Author", s: "Amazon · Mobile marketing" },
  { l: "stage", t: "Collision Speaker", s: "2019 · MyWiFi Networks", onClick: () => setCollisionOpen(true), badge: "▸ play" },
  { l: "teach", t: "Udemy Instructor", s: "WiFi marketing · 5K+ students" },
  { l: "open", t: "Open Source", s: "Commander · ClaudeSwap" },
  { l: "base", t: "Toronto, Canada", s: "Self-hosted · cloud · 24/7" }];

  return (
    <section data-section="creds" className="section-pad" style={{ paddingTop: 40, paddingBottom: 60 }}>
      <div className="container">
        <div className="box-head">
          <window.AsciiBadge glyph="hash" count={3} />
          <span>// 10 — credentials</span>
          <span className="rule" />
        </div>
        <Reveal>
          <div className="creds-grid">
            {items.map((it, i) =>
            <div
              key={i}
              className="cred"
              onClick={it.onClick}
              role={it.onClick ? "button" : undefined}
              tabIndex={it.onClick ? 0 : undefined}
              onKeyDown={it.onClick ? (e) => { if (e.key === "Enter" || e.key === " ") it.onClick(); } : undefined}
              style={it.onClick ? { cursor: "pointer", position: "relative" } : undefined}
            >
                <div className="label">▸ {it.l}</div>
                <div className="title">{it.t}</div>
                <div className="sub">{it.s}</div>
                {it.badge && <div style={{ position: "absolute", top: 12, right: 14, fontSize: 10, color: "var(--amber)", letterSpacing: "0.18em", textTransform: "uppercase" }}>{it.badge}</div>}
              </div>
            )}
          </div>
        </Reveal>
        <VideoModal
          open={collisionOpen}
          onClose={() => setCollisionOpen(false)}
          videoId="SNIXpZp8f-U"
          meta="▸ Collision · 2019 · MyWiFi Networks · captive-portal economics"
        />
      </div>
    </section>);

}

/* ---------- WORK WITH ME ---------- */
function WorkWithMe() {
  const [mode] = useKzMode();
  if (mode === "exec") return null;

  return (
    <section id="consulting" data-section="consulting" className="section-pad">
      <div className="container">
        <div className="box-head">
          <window.AsciiBadge glyph="bracket" count={3} />
          <span>// 12 — work with me</span>
          <span className="rule" />
        </div>
        <Reveal>
          <div className="work-card">
            <div className="win-title">
              <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
              <span>~/engagements/2026-q3</span>
              <span className="meta">SLOTS · 2 OPEN</span>
            </div>
            <div className="body">
              <h2 className="h-section" style={{ maxWidth: "22ch" }}>
                want agent teams in <span className="amber">your business?</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "62ch" }}>
                Which one job should AI do for you? <a href="/work-with-me" style={{ color: "var(--amber)", textDecoration: "none" }}>See how we can work together →</a>
              </p>

              <div className="row gap-12 mt-32">
                <a className="btn btn-primary" href="https://www.readyiq.ai/book" target="_blank" rel="noreferrer"
                   style={{ background: "#7C3AED", borderColor: "#7C3AED", color: "#fff" }}>▸ book an AI Audit</a>
                <a className="btn" href="#contact">$ book 20-min intro</a>
              </div>
            </div>
            <div className="win-foot">
              <span>NEXT AVAILABILITY · 2026·Q3</span>
              <span>2 / 4 SLOTS OPEN</span>
              <span>EOL</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>);

}

/* ---------- FOOTER SUBSCRIBE ---------- */
function FooterSubscribe() {
  const [email, setEmail] = React.useState("");
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/newsletter-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "footer" }),
      });
      if (res.ok) {
        setDone(true);
        window.plausible?.("email_signup", { props: { audience: "kevinzai", source: "footer" } });
      } else {
        const data = await res.json().catch(() => ({}));
        setErr(data.error === "invalid_email" ? "invalid email" : "try again");
      }
    } catch (_) {
      setErr("network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="foot-h">subscribe</div>
      <p className="dim2" style={{ fontSize: 12, marginBottom: 12 }}>
        The weekly playbook. Sundays, 8 AM ET.
      </p>
      {done ? (
        <p className="dim2" style={{ fontSize: 12 }}>✓ subscribed</p>
      ) : (
        <form className="news-row" onSubmit={handleSubmit} style={{ marginTop: 0 }}>
          <input
            className="news-input"
            placeholder="you@operator.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            aria-label="Email address for The Agent Report" />
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? "..." : "→"}
          </button>
        </form>
      )}
      {err && <p style={{ fontSize: 11, color: "var(--red, #f87171)", marginTop: 4 }}>{err}</p>}
    </div>
  );
}

/* ---------- FOOTER ---------- */
function Footer() {
  return (
    <footer>
      <div className="container">
        <div className="footer-grid">
          <div>
            <div className="row" style={{ gap: 10 }}>
              <span style={{ width: 26, height: 26, border: "1px solid var(--amber)", color: "var(--amber)", display: "inline-flex", alignItems: "center", placeContent: "center", fontWeight: 700, fontSize: 12, background: "rgba(245,183,61,0.06)" }}>KZ</span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>kevinz.ai</span>
            </div>
            <p className="dim2 mt-16" style={{ fontSize: 13, lineHeight: 1.65, maxWidth: 280 }}>
              Building in public. No fluff. Architecting AI agent teams from Toronto.
            </p>
            <pre className="hr-ascii mt-16">────────────────────────────────</pre>
            <div className="dim mt-12" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              <ThinkingBadge />
            </div>
          </div>
          <div>
            <div className="foot-h">connect</div>
            <ul className="foot-list">
              <li><a href="https://x.com/KZic" target="_blank" rel="noreferrer">X / @KZic →</a></li>
              <li><a href="https://linkedin.com/in/kzicherman" target="_blank" rel="noreferrer">LinkedIn →</a></li>
              <li><a href="https://github.com/KevinZai" target="_blank" rel="noreferrer">GitHub →</a></li>
              <li><a href="mailto:hi@kevinz.ai">Email →</a></li>
            </ul>
          </div>
          <div>
            <div className="foot-h">products</div>
            <ul className="foot-list">
              <li><a href="#commander">Commander →</a></li>
              <li><a href="https://readyiq.ai" target="_blank" rel="noreferrer">ReadyIQ →</a></li>
              <li><a href="https://dmhub.ai" target="_blank" rel="noreferrer">DMHub →</a></li>
              <li><a href="https://mywifinetworks.com" target="_blank" rel="noreferrer">MyWiFi →</a></li>
              <li><a href="https://guestnetworks.com" target="_blank" rel="noreferrer">Guest Networks →</a></li>
            </ul>
          </div>
          <div>
            <div className="foot-h">writing</div>
            <ul className="foot-list">
              <li><a href="/posts">All Posts →</a></li>
              <li><a href="#newsletter">Agent Report →</a></li>
              <li><a href="https://github.com/KevinZai/openclaw-automation" target="_blank" rel="noreferrer">openclaw-automation →</a></li>
              <li><a href="/uses">/uses →</a></li>
            </ul>
          </div>
          <FooterSubscribe />
        </div>
        <div className="foot-bottom">
          <span>© 2026 kevin zicherman · toronto, ca</span>
          <span className="center">built with commander · operator on call</span>
          <span className="right">v2.6.0 · build #38421</span>
        </div>
      </div>
    </footer>);

}

Object.assign(window, {
  AsciiBadge, AsciiRule, AsciiCover, ProofSection, ThinkingBadge,
  TopBar, Hero, BizCard, StatsBar, Toolchain, Newsletter, Commander, Projects, Writing,
  Credentials, WorkWithMe, Footer, VerifiedStrip, ExecCoverBanner, ExecProfileHeader,
  ExecAbout, ExecFeatured, ExecExperience, Reveal, CountUp, Heatmap, useKzMode
});

export {
  AsciiBadge,
  AsciiRule,
  AsciiCover,
  ProofSection,
  ThinkingBadge,
  TopBar,
  Hero,
  BizCard,
  StatsBar,
  Toolchain,
  Newsletter,
  Commander,
  Projects,
  Writing,
  Credentials,
  WorkWithMe,
  Footer,
  VerifiedStrip,
  ExecCoverBanner,
  ExecProfileHeader,
  ExecAbout,
  ExecFeatured,
  ExecExperience,
  Reveal,
  CountUp,
  Heatmap,
  useKzMode
};
